// ════════════════════════════════════════════════════════════════════════════
// WHITE CARD EFFECTS
// See js/effects/registry.js for the registerCardEffect() API and the list
// of available triggers.
// ════════════════════════════════════════════════════════════════════════════

// ── Shared helpers (white-card-local; not part of the registry API) ──

// Mutates each target's AP modifier field and mirrors the change to whichever
// client actually owns each uid — FIELD_AP_MOD's receiving handler searches
// both playerField and oppField (see js/game.js's NET.onFieldApMod), so this
// is safe to call for friendly OR enemy targets. `field` is 'tempApMod'
// ("during this turn") or 'battleApMod' ("during this battle").
function applyApMod(targets, delta, field) {
  if (!targets || targets.length === 0) return;
  // passiveApReduceImmune: skip units whose linked pilot blocks negative AP mods (e.g. GD01-090 Duo Maxwell)
  let actual = targets;
  if (delta < 0 && (field === 'tempApMod' || field === 'battleApMod')) {
    actual = targets.filter(c => {
      if (!c.pilot) return true;
      const h = window.CardEffects?.[c.pilot.cardNo];
      if (h?.passiveApReduceImmune?.(c.pilot, c)) {
        addLog(`${c.name} — AP reduction blocked (linked pilot grants immunity).`);
        return false;
      }
      return true;
    });
  }
  if (actual.length === 0) return;
  actual.forEach(c => { c[field] = (c[field] || 0) + delta; });
  NET.send({ type: 'FIELD_AP_MOD', uids: actual.map(c => c.uid), delta, field });
}

// Directly modifies card.hp by delta and mirrors via FIELD_HP_MOD. For
// recovery (delta > 0): caps each card so effectiveHp never exceeds
// effectiveMaxHp (DB base HP + pilot bonus). Skips cards already at max.
// Sends one FIELD_HP_MOD per card with the actual applied delta so the peer
// always mirrors the capped value, not the requested value.
function applyHpMod(targets, delta) {
  if (!targets || targets.length === 0) return;
  targets.forEach(c => {
    let actual = delta;
    if (delta > 0) {
      const headroom = effectiveMaxHp(c) - effectiveHp(c);
      if (headroom <= 0) return; // already at max HP, skip
      actual = Math.min(delta, headroom);
    }
    c.hp = (parseInt(c.hp) || 0) + actual;
    if (actual > 0 && effectiveHp(c) >= effectiveMaxHp(c)) c.damaged = false;
    NET.send({ type: 'FIELD_HP_MOD', uids: [c.uid], delta: actual });
    // Pilot passive: "when this Unit recovers HP" (e.g. GD02-085 Four Murasame)
    if (actual > 0 && c.pilot) {
      const _hpPilotH = window.CardEffects?.[c.pilot.cardNo];
      if (_hpPilotH?.onUnitHpRecovered) _hpPilotH.onUnitHpRecovered(c.pilot, c);
    }
    // Unit-level passive: "when THIS Unit recovers HP" (e.g. EB01-004 Gundam
    // Barbatos Lupus Rex), independent of any paired pilot.
    if (actual > 0) {
      window.CardEffects?.[c.cardNo]?.onSelfHpRecovered?.(c);
    }
  });
}

// For "recovers N HP when THIS Unit destroys an enemy Unit with battle
// damage" hooks (onLinkedUnitDestroysEnemy/onUnitDestroysEnemy, fired via
// notifyUnitDestroyedByBattle from runDamageStep's "Defender destroyed?"
// branch — e.g. GD05-085 Amuro Ray, ST07-005 Gundam Dynames Lv4). That
// branch runs BEFORE this same combat exchange applies the destroying
// Unit's own counter-damage (runDamageStep's later "Attacker destroyed?"
// branch) — so calling applyHpMod synchronously from one of these hooks
// computes headroom against the Unit's still-undamaged pre-exchange HP and
// silently caps the recovery to 0 whenever the Unit entered the battle at
// full health (the common case). Deferring to the next tick lets the whole
// synchronous combat resolution — including that counter-damage — finish
// first, so the cap sees the Unit's real post-exchange HP.
function deferredApplyHpMod(targets, delta) {
  setTimeout(() => { applyHpMod(targets, delta); render(); }, 0);
}

// True when the current attack's declared target is "the enemy player" as
// printed on cards (e.g. GD01-059 Zee Zulu, ST08-006 Penelope, GD05-092
// Auel Neider, GD04-049 Gundam DX) — that phrase covers attacking the
// enemy's Base, a Shield, OR a genuine Direct Attack (no Base/Shields
// left); the only thing it excludes is attacking an enemy Unit. isShield
// and isDirect are set directly on ctx for those two synthetic targets;
// a real Base attack sets neither, so it's identified the same way
// GD01-059 originally did — the defender simply isn't in oppField.
function isAttackingEnemyPlayer(ctx) {
  if (!ctx || !ctx.defender) return false;
  if (ctx.isShield || ctx.isDirect) return true;
  return !state.oppField.some(c => c && c.uid === ctx.defender.uid);
}

// Removes a real enemy Unit from the opponent's field and tells their client
// to return it to their own hand — only they have the authoritative card
// data for it (mirrors GD03-118's existing UNIT_BOUNCE usage). In a bot
// match there's no second client to receive that message and rebuild its
// own hand copy (NET.send silently no-ops with no socket open) — CONFIRMED
// BUG found via live play: the bounced card simply vanished instead of
// returning to the bot's hand, so it could never be redeployed. BotEngine's
// botReceiveBouncedUnit mirrors NET.onUnitBounce's own body directly against
// oppHand/oppTempDeck instead.
function bounceEnemyUnit(target) {
  const idx = state.oppField.findIndex(c => c && c.uid === target.uid);
  if (idx !== -1) state.oppField[idx] = null;
  if (state.isBotMatch) {
    BotEngine.botReceiveBouncedUnit(target);
  } else {
    NET.send({ type: 'UNIT_BOUNCE', uid: target.uid });
  }
  const bounceToast = (name) => {
    if (currentEffectSourceCard) broadcastCardToast(`${currentEffectSourceCard.name} effect bounced ${name} to hand.`);
    else broadcastCardToast(`${name} was added to hand.`);
  };
  if (!isUnitToken(target)) {
    bounceToast(target.name);
    if (target.pilot) bounceToast(target.pilot.name);
  } else if (target.pilot) {
    bounceToast(target.pilot.name);
  }
}

// Same as bounceEnemyUnit, but for "return to the bottom of its owner's
// deck" effects (e.g. GD05-002 Strike Freedom Gundam) instead of "return to
// hand". Only the opponent's own client has their authoritative tempDeck, so
// this just removes the unit from my view of their field and tells them
// which uid to place at the bottom of their own deck. Same bot-match gap
// (and fix) as bounceEnemyUnit above — no second client to act on
// UNIT_TO_DECK_BOTTOM, so BotEngine.botReceiveUnitToDeckBottom does that
// work directly against oppTempDeck/oppHand instead.
function bounceEnemyUnitToDeckBottom(target) {
  const idx = state.oppField.findIndex(c => c && c.uid === target.uid);
  if (idx !== -1) state.oppField[idx] = null;
  if (state.isBotMatch) {
    BotEngine.botReceiveUnitToDeckBottom(target);
  } else {
    NET.send({ type: 'UNIT_TO_DECK_BOTTOM', uid: target.uid });
  }
  if (target.pilot) {
    if (currentEffectSourceCard) broadcastCardToast(`${currentEffectSourceCard.name} effect bounced ${target.pilot.name} to hand.`);
    else broadcastCardToast(`${target.pilot.name} was added to hand.`);
  }
}

// Sets a Unit/Base's rested flag and mirrors it via UNIT_REST so the actual
// controller's own authoritative copy (and thus their Block Step eligibility
// checks) stays in sync.
// Also fires the 'unrest' pilot trigger when a player field unit transitions
// rested → active by an effect (e.g. Graham Aker GD03-098). Only fires for
// playerField units so the effect picker only opens on the owner's client.
function setRested(target, rested) {
  // cantBeSetActive: a permanent restriction printed on the card itself
  // (e.g. T-014 Ad Balloon: "This Unit can't be set as active") — see
  // registry.js for the hook contract. Blocks every card-effect-driven
  // attempt to un-rest this specific card; the Start Phase auto-unrest path
  // (registerAction('UNREST_ALL', ...), game.js) doesn't call setRested at
  // all and carries its own identical check.
  if (rested === false && target.rested && window.CardEffects?.[target.cardNo]?.cantBeSetActive?.(target)) {
    addLog(`${target.name} can't be set as active.`);
    return;
  }
  // preventsEnemyUnrestByEffect: a standing passive on the OPPOSING field
  // (e.g. GD06-037 Reginlaze Julia: "[During Pair] Enemy Units can't be set
  // as active by enemy effects") blocks every card-effect-driven attempt to
  // un-rest a Unit on this side. Only checked for Units (never the Base —
  // setRested() is also called for e.g. GD03-119's "set active" Base
  // target), identified the same way the pilot's 'unrest' trigger below
  // does. The Start Phase auto-unrest path doesn't call setRested at all
  // (same carve-out as cantBeSetActive above), so this never blocks the
  // normal turn reset — only effect-driven un-resting, matching the card's
  // "by enemy effects" wording.
  const targetIsUnit = state.playerField.some(c => c && c.uid === target.uid);
  if (rested === false && target.rested && targetIsUnit) {
    const blocker = state.oppField.find(c => c && window.CardEffects?.[c.cardNo]?.preventsEnemyUnrestByEffect?.(c));
    if (blocker) {
      addLog(`${target.name} can't be set as active — ${blocker.name} prevents it.`);
      return;
    }
  }
  const wasRested = target.rested;
  target.rested = rested;
  NET.send({ type: 'UNIT_REST', uid: target.uid, rested });
  // Only the acting client calls setRested (see NET.onUnitRest — the peer
  // just applies the mirrored flag directly, never re-calling this), so no
  // double-toast risk. currentEffectSourceCard is only non-null while a
  // registered card-effect handler (or its picker callback) is actually
  // resolving — combat's own "rest the attacker" and Start Phase's "unrest
  // all" don't go through here at all, so this can't misfire for those.
  if (!wasRested && rested && currentEffectSourceCard) {
    broadcastCardToast(`${currentEffectSourceCard.name} effect rested ${target.name}.`);
  }
  if (wasRested && !rested && target.pilot) {
    if (state.playerField.some(c => c && c.uid === target.uid)) {
      triggerCardEffect(target.pilot, 'unrest', { unit: target });
    }
  }
  if (!wasRested && rested && state.playerField.some(c => c && c.uid === target.uid)) {
    window.CardEffects?.[target.cardNo]?.onRestedByEffect?.(target, {});
  }
}

// Single-select "choose 1 enemy Unit ... return it to its owner's hand"
// pattern shared by several white Commands/Command Pilots.
function pickAndBounceOne(card, candidates, promptText, onDone) {
  if (candidates.length === 0) {
    addLog(`${card.name} — no valid enemy Unit to return. No effect.`);
    onDone();
    return;
  }
  showCardPickerModal(card.name, promptText, candidates, (target) => {
    if (target) {
      bounceEnemyUnit(target);
      addLog(isUnitToken(target)
        ? `${card.name} — ${target.name} (token) is removed from the game.`
        : `${card.name} — returned ${target.name} to its owner's hand.`);
      render();
      setupDropZones();
    }
    onDone();
  }, card);
}

// "Choose 1 to 2 enemy Units ... return them to their owners' hands" (GD04-117).
function pickAndBounceMulti(card, candidates, promptText, range, onDone) {
  if (candidates.length === 0) {
    addLog(`${card.name} — no valid enemy Units to return. No effect.`);
    onDone();
    return;
  }
  showCardPickerModalMulti(card.name, promptText, candidates, range, (targets) => {
    targets.forEach(target => {
      bounceEnemyUnit(target);
      addLog(isUnitToken(target)
        ? `${card.name} — ${target.name} (token) is removed from the game.`
        : `${card.name} — returned ${target.name} to its owner's hand.`);
    });
    if (targets.length > 0) { render(); setupDropZones(); }
    onDone();
  }, card);
}

// Single-select "choose 1 Unit ... it gets AP+/-N during this turn/battle".
function pickAndApplyApMod(card, candidates, delta, field, promptText, onDone) {
  if (candidates.length === 0) {
    addLog(`${card.name} — no valid target. No effect.`);
    onDone();
    return;
  }
  showCardPickerModal(card.name, promptText, candidates, (target) => {
    if (target) {
      applyApMod([target], delta, field);
      const sign = delta > 0 ? '+' : '';
      const span = field === 'battleApMod' ? 'battle' : 'turn';
      addLog(`${card.name} — ${target.name} gets AP${sign}${delta} during this ${span}.`);
      render();
      setupDropZones();
    }
    onDone();
  }, card);
}

// GD01-118 "Overflowing Affection" — [Main] Draw 2. Then, discard 1.
registerCardEffect('GD01-118', {
  main: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(2, true);
    drawn.forEach(c => state.hand.push(c));
    addLog(`${card.name} — Drew ${drawn.length} card${drawn.length !== 1 ? 's' : ''}.`);
    render();
    setupDropZones();

    showHandDiscardModal(() => {
      broadcastCounters();
      ctx.onDone();
    }, {
      needed: 1,
      exact: true,
      title: card.name,
      question: 'Select 1 card to discard.',
    });
  },
});

// GD02-117 "A New Sign"
// [Burst] Choose 1 (AEUG) Base card from your trash. Add it to your hand.
// [Main] Draw 3. Then, discard 2.
registerCardEffect('GD02-117', {
  burst: (card) => {
    const candidates = state.playerTrash.filter(c =>
      c.type === 'base' && (c.trait || '').includes('AEUG'));
    if (candidates.length === 0) return;

    showCardPickerModal(
      card.name,
      'Choose 1 (AEUG) Base card from your trash to add to your hand.',
      candidates,
      (picked) => {
        if (!picked) return;
        state.playerTrash = state.playerTrash.filter(c => c.uid !== picked.uid);
        state.trashCount = Math.max(0, state.trashCount - 1);
        updateTempState(picked.cardNo, picked.copyVer, 'hand');
        state.hand.push(picked);
        addLog(`${card.name} — added ${picked.name} from trash to hand.`);
        broadcastCardToast(`${picked.name} was added from trash to hand due to ${card.name} effect.`);
        glowHandCard(picked.uid);
        broadcastCounters();
        render();
        setupDropZones();
      },
      card
    );
  },
  main: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(3, true);
    drawn.forEach(c => state.hand.push(c));
    addLog(`${card.name} — Drew ${drawn.length} card${drawn.length !== 1 ? 's' : ''}.`);
    render();
    setupDropZones();

    showHandDiscardModal(() => {
      broadcastCounters();
      ctx.onDone();
    }, {
      needed: 2,
      exact: true,
      title: card.name,
      question: 'Select 2 cards to discard.',
    });
  },
});

// GD02-120 "Aspiring Pilot"
// [Action] Choose 1 of your (AEUG) Units/Bases. It recovers 2 HP.
function gd02120Candidates() {
  return [
    ...state.playerField.filter(c => c && c.damaged && (c.trait || '').includes('AEUG')),
    ...(state.playerBase && state.playerBase.damaged && (state.playerBase.trait || '').includes('AEUG') ? [state.playerBase] : []),
  ];
}
registerCardEffect('GD02-120', {
  canActivate: () => gd02120Candidates().length > 0,
  action: (card, ctx) => {
    const candidates = gd02120Candidates();
    if (candidates.length === 0) { ctx.onDone(); return; }

    showCardPickerModal(
      card.name,
      'Choose 1 of your (AEUG) Units/Bases. It recovers 2 HP.',
      candidates,
      (picked) => {
        if (picked) {
          const entry = getCardDbEntry(picked.img);
          const maxHp = entry ? (parseStatValue(entry.hp) ?? 0) : 0;
          const newHp = Math.min((parseInt(picked.hp) || 0) + 2, maxHp);
          picked.hp = newHp;
          if (newHp >= maxHp) picked.damaged = false;
          addLog(`${card.name} — ${picked.name} recovers 2 HP (HP now: ${newHp}).`);
          render();
          setupDropZones();
        }
        ctx.onDone();
      },
      card
    );
  },
});

// GD03-118 "Awakened Potential"
// [Burst] Add this card to your hand. (handled generically — literal
// "Add this card to your hand" pattern, see resolveMyShieldHit's hasBurst.)
// [Action] Choose 1 rested enemy Unit that is Lv.4 or lower. Return it to
// its owner's hand. Then, if there are 2+ cards named "Awakened Potential"
// in your trash, you may choose 1 friendly Unit — it gains <Blocker>
// during this turn.
function gd03118BounceCandidates() {
  return state.oppField.filter(c =>
    c && c.rested && (c.type === 'unit' || c.type === 'unit-token') && (parseInt(c.level) || 0) <= 4);
}
function gd03118BlockerCandidates(excludeUid) {
  const copies = state.playerTrash.filter(c =>
    (c.name || '').includes('Awakened Potential') && c.uid !== excludeUid
  ).length;
  return copies >= 2 ? state.playerField.filter(c => c) : [];
}
registerCardEffect('GD03-118', {
  // The bounce is mandatory and must resolve first ("Then" sequencing) —
  // activatable only when there is a valid bounce target.
  canActivate: () => gd03118BounceCandidates().length > 0,
  action: (card, ctx) => {
    function maybeGrantBlocker() {
      const blockerCandidates = gd03118BlockerCandidates(card.uid);
      if (blockerCandidates.length === 0) { ctx.onDone(); return; }

      showCardPickerModal(
        card.name,
        'You may choose 1 friendly Unit. It gains Blocker during this turn.',
        blockerCandidates,
        (picked) => {
          if (picked) {
            picked.tempKeywords = [...(picked.tempKeywords || []), 'Blocker'];
            addLog(`${card.name} — ${picked.name} gains <Blocker> during this turn.`);
            if (state.networked) NET.send({ type: 'TEMP_KEYWORD_ADD', uid: picked.uid, keyword: 'Blocker' });
            render();
            setupDropZones();
          }
          ctx.onDone();
        },
        card
      );
    }

    showCardPickerModal(
      card.name,
      "Choose 1 rested enemy Unit (Lv.4 or lower) to return to its owner's hand.",
      gd03118BounceCandidates(),
      (target) => {
        if (target) {
          bounceEnemyUnit(target);
          addLog(isUnitToken(target)
            ? `${card.name} — ${target.name} (token) is removed from the game.`
            : `${card.name} — returned ${target.name} to its owner's hand.`);
          render();
          setupDropZones();
          maybeGrantBlocker(); // "Then" — only fires after a bounce resolved
        } else {
          ctx.onDone(); // player cancelled bounce — no Blocker step
        }
      },
      card
    );
  },
});

// GD03-119 "Awkward Approach"
// [Main] Choose 1 rested friendly Base. Set it as active. If you do, all
// enemy Units get AP-1 during this turn.
registerCardEffect('GD03-119', {
  canActivate: () => !!state.playerBase && state.playerBase.rested,
  main: (card, ctx) => {
    const base = state.playerBase;
    if (!base || !base.rested) {
      addLog(`${card.name} — no rested friendly Base to set active. No effect.`);
      ctx.onDone();
      return;
    }

    setRested(base, false);
    const targets = state.oppField.filter(c => c);
    applyApMod(targets, -1, 'tempApMod');
    addLog(`${card.name} — set ${base.name} active. All enemy Units get AP-1 during this turn.`);
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// GD01-117 "The Witch and the Bronze"
// [Burst] Activate this card's [Main].
// [Main]/[Action] Choose 1 enemy Unit with 5 or less HP. Return it to its
// owner's hand.
function gd01117Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 5);
  pickAndBounceOne(card, candidates, "Choose 1 enemy Unit with 5 or less HP. Return it to its owner's hand.", onDone);
}
registerCardEffect('GD01-117', {
  canActivate: () => state.oppField.some(c => c && effectiveHp(c) <= 5),
  burst: (card, ctx) => gd01117Effect(card, ctx?.onDone ?? (() => {})),
  main: (card, ctx) => gd01117Effect(card, ctx.onDone),
  action: (card, ctx) => gd01117Effect(card, ctx.onDone),
});

// GD01-119 "Iron-Fisted Discipline"
// [Main]/[Action] Choose 1 enemy Unit that is Lv.4 or lower. It gets AP-2
// during this turn.
function gd01119Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
  pickAndApplyApMod(card, candidates, -2, 'tempApMod', "Choose 1 enemy Unit (Lv.4 or lower). It gets AP-2 during this turn.", onDone);
}
registerCardEffect('GD01-119', {
  canActivate: () => state.oppField.some(c => c && (parseInt(c.level) || 0) <= 4),
  main: (card, ctx) => gd01119Effect(card, ctx.onDone),
  action: (card, ctx) => gd01119Effect(card, ctx.onDone),
});

// ─── EB01-083 · SP Conversion Chips ───────────────────────────────────────
// [Action] If it is your opponent's turn, choose 1 Unit. It gets AP+3
// during this turn.
registerCardEffect('EB01-083', {
  canActivate: () => !state.myTurn && state.playerField.some(c => c),
  action: (card, ctx) => {
    if (state.myTurn) {
      addLog(`${card.name} — not your opponent's turn. No effect.`);
      ctx.onDone(); return;
    }
    const candidates = state.playerField.filter(c => c);
    pickAndApplyApMod(card, candidates, 3, 'tempApMod', "Choose 1 Unit. It gets AP+3 during this turn.", ctx.onDone);
  },
});

// GD01-120 "Naval Bombardment"
// [Burst] Choose 1 enemy Unit. It gets AP-3 during this turn.
// [Action] Choose 1 friendly Unit with <Blocker>. It gets AP+3 during this turn.
registerCardEffect('GD01-120', {
  canActivate: () => state.playerField.some(c => c && hasNativeKeyword(c, 'Blocker')),
  burst: (card) => {
    const candidates = state.oppField.filter(c => c);
    pickAndApplyApMod(card, candidates, -3, 'tempApMod', "Choose 1 enemy Unit. It gets AP-3 during this turn.", () => {});
  },
  action: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && hasNativeKeyword(c, 'Blocker'));
    pickAndApplyApMod(card, candidates, 3, 'tempApMod', "Choose 1 friendly Unit with <Blocker>. It gets AP+3 during this turn.", ctx.onDone);
  },
});

// GD01-121 "Mechanic Modifications"
// [Burst] Activate this card's [Main].
// [Main] Choose 1 rested Unit with <Blocker>. Set it as active. It can't
// attack during this turn.
// (Card text doesn't say "friendly" or "enemy" — restricted to friendly
// Units per the user's explicit answer when this was clarified.)
function gd01121Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && c.rested && hasNativeKeyword(c, 'Blocker'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no rested friendly Unit with <Blocker>. No effect.`);
    onDone();
    return;
  }
  showCardPickerModal(card.name, "Choose 1 rested friendly Unit with <Blocker>. Set it as active. It can't attack during this turn.", candidates, (target) => {
    if (target) {
      setRested(target, false);
      target.tempCantAttack = true;
      addLog(`${card.name} — set ${target.name} active. It can't attack during this turn.`);
      render();
      setupDropZones();
    }
    onDone();
  }, card);
}
registerCardEffect('GD01-121', {
  canActivate: () => state.playerField.some(c => c && c.rested && hasNativeKeyword(c, 'Blocker')),
  burst: (card, ctx) => gd01121Effect(card, ctx?.onDone ?? (() => {})),
  main: (card, ctx) => gd01121Effect(card, ctx.onDone),
});

// ─── EB01-084 · 30cm Cannon (APFSDS Round) ────────────────────────────────
// [Main]/[Action] Choose 1 Unit with <Blocker>. Set it as active. It can't
// attack during this turn.
// [Pilot] Demeziere Sonnen
// (Same "rested friendly" convention as GD01-121 Mechanic Modifications.)
function eb01084Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && c.rested && hasNativeKeyword(c, 'Blocker'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no rested friendly Unit with <Blocker>. No effect.`);
    onDone();
    return;
  }
  showCardPickerModal(card.name, "Choose 1 rested friendly Unit with <Blocker>. Set it as active. It can't attack during this turn.", candidates, (target) => {
    if (target) {
      setRested(target, false);
      target.tempCantAttack = true;
      addLog(`${card.name} — set ${target.name} active. It can't attack during this turn.`);
      render();
      setupDropZones();
    }
    onDone();
  }, card);
}
registerCardEffect('EB01-084', {
  canActivate: () => state.playerField.some(c => c && c.rested && hasNativeKeyword(c, 'Blocker')),
  main:   (card, ctx) => eb01084Effect(card, ctx.onDone),
  action: (card, ctx) => eb01084Effect(card, ctx.onDone),
});

// GD01-122 "Covert Operative"
// [Main] Choose 1 enemy Unit with 2 or less HP. Return it to its owner's
// hand. If you have a Link Unit in play, choose 1 enemy Unit with 4 or less
// HP instead.
function gd01122Candidates() {
  const hasLink = state.playerField.some(isLinkUnit);
  const maxHp = hasLink ? 4 : 2;
  return state.oppField.filter(c => c && effectiveHp(c) <= maxHp);
}
registerCardEffect('GD01-122', {
  canActivate: () => gd01122Candidates().length > 0,
  main: (card, ctx) => {
    const hasLink = state.playerField.some(isLinkUnit);
    const maxHp = hasLink ? 4 : 2;
    pickAndBounceOne(card, gd01122Candidates(), `Choose 1 enemy Unit with ${maxHp} or less HP. Return it to its owner's hand.`, ctx.onDone);
  },
});

// GD02-118 "Heart Set on Revenge"
// [Action] Choose 1 enemy Unit with 4 or less HP battling a friendly Unit
// with <Blocker>. Return it to its owner's hand.
// Takes the raw battle ctx ({ attacker, attackerSide, defenderSide, defender,
// ... }) directly — NOT the 'action' trigger's wrapper ctx. canActivate is
// called with that raw battle object (see registry.js's canActivate(card,
// ctx) doc), while the action handler below unwraps its own ctx.battle
// before calling in, so both call sites agree on this function's shape.
function gd02118Target(battle) {
  if (!battle) return null;
  // Enemy attacks a friendly Blocker unit — target is the enemy attacker
  if (battle.defenderSide === 'player' && battle.defender &&
      hasNativeKeyword(battle.defender, 'Blocker') && battle.attackerSide === 'opp' &&
      effectiveHp(battle.attacker) <= 4) {
    return battle.attacker;
  }
  // Friendly Blocker unit attacks an enemy unit — target is the enemy defender
  if (battle.attackerSide === 'player' && battle.attacker &&
      hasNativeKeyword(battle.attacker, 'Blocker') && battle.defenderSide === 'opp' &&
      battle.defender && !battle.defender.isShield && !battle.defender.isDirect &&
      effectiveHp(battle.defender) <= 4) {
    return battle.defender;
  }
  return null;
}
registerCardEffect('GD02-118', {
  canActivate: (card, ctx) => !!gd02118Target(ctx),
  action: (card, ctx) => {
    const target = gd02118Target(ctx.battle);
    if (!target) {
      addLog(`${card.name} — no enemy Unit (4 or less HP) battling a friendly Unit with <Blocker>. No effect.`);
      ctx.onDone();
      return;
    }
    bounceEnemyUnit(target);
    addLog(`${card.name} — returned ${target.name} to its owner's hand.`);
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// GD02-119 "Persistent and Fortitudinous"
// [Action] If you have a (Gjallarhorn) Link Unit in play, choose 1 enemy
// Unit. It gets AP-3 during this battle.
function gd02119HasGjallarhornLink() {
  return state.playerField.some(c => c && isLinkUnit(c) && (c.trait || '').includes('Gjallarhorn'));
}
registerCardEffect('GD02-119', {
  canActivate: () => gd02119HasGjallarhornLink() && state.oppField.some(c => c),
  action: (card, ctx) => {
    if (!gd02119HasGjallarhornLink()) {
      addLog(`${card.name} — no (Gjallarhorn) Link Unit in play. No effect.`);
      ctx.onDone();
      return;
    }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units in play. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, "Choose 1 enemy Unit. It gets AP-3 during this battle.", candidates, (target) => {
      if (target) {
        applyApMod([target], -3, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP-3 during this battle.`);
        render();
        setupDropZones();
      }
      ctx.onDone();
    }, card);
  },
});

// ─── ST10-015 · Diffuse Beam Cannon ───────────────────────────────────────
// [Action] If a friendly (G Generation) Unit is in play, choose 1 enemy
// Unit. It gets AP-3 during this battle.
// [Pilot] Claire Heathrow
// Restricted to the battling enemy Unit only (the current Action Step's
// opposing combatant) — same battle-ctx unwrap convention as GD02-118
// (canActivate receives the raw battle ctx; action unwraps ctx.battle).
function st10015EnemyTarget(battle) {
  if (!battle) return null;
  if (battle.attackerSide === 'player' && battle.defenderSide === 'opp' && battle.defender &&
      !battle.defender.isShield && !battle.defender.isDirect &&
      (battle.defender.type === 'unit' || battle.defender.type === 'unit-token')) {
    return battle.defender;
  }
  if (battle.defenderSide === 'player' && battle.attackerSide === 'opp' && battle.attacker) {
    return battle.attacker;
  }
  return null;
}
function st10015HasGGeneration() {
  return state.playerField.some(c => c && (c.trait || '').includes('(G Generation)'));
}
registerCardEffect('ST10-015', {
  canActivate: (card, ctx) => st10015HasGGeneration() && !!st10015EnemyTarget(ctx),
  action: (card, ctx) => {
    if (!st10015HasGGeneration()) {
      addLog(`${card.name} — no friendly (G Generation) Unit in play. No effect.`);
      ctx.onDone();
      return;
    }
    const target = st10015EnemyTarget(ctx.battle);
    if (!target) {
      addLog(`${card.name} — no battling enemy Unit found. No effect.`);
      ctx.onDone();
      return;
    }
    applyApMod([target], -3, 'battleApMod');
    addLog(`${card.name} — ${target.name} gets AP-3 during this battle.`);
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// GD01-096 "Cagalli Yula Athha" — Pilot
// While this Unit is white, it gains <Blocker>.
// Passive — granted permanently on pair via pilotKeywords (not tempKeywords,
// so UNREST_ALL does not clear it). pilotKeywords is cleared automatically
// when the pilot leaves the unit (unit destroyed/bounced removes the unit
// entirely; Ali al-Saachez unpair explicitly clears pilotKeywords).
// Because PAIR_PILOT fires triggerCardEffect before the PLAY_CARD network send,
// the opponent receives the card with pilotKeywords already set — no extra
// network message is needed.
registerCardEffect('GD01-096', {
  pair: (card, ctx) => {
    const unit = ctx.unit;
    if (!unit) return;
    if ((unit.color || '').toLowerCase() !== 'white') {
      addLog(`${card.name} — paired unit is not white. No Blocker granted.`);
      return;
    }
    unit.pilotKeywords = [...(unit.pilotKeywords || []), 'Blocker'];
    addLog(`${card.name} — ${unit.name} gains <Blocker> while this Pilot is linked.`);
  },
});

// GD01-098 "Elan Ceres (Enhanced Person Number 4)" — Pilot
// [Activate･Action][Once per Turn] If an enemy Unit with 1 or less AP is in
// play, this Unit recovers 1 HP.
// Activated via resolvePilotActionActivate in game.js — fires during either
// side's Action Step. passiveUsedThisTurn gates the once-per-turn limit;
// cleared by UNREST_ALL at the start of the next turn.
registerCardEffect('GD01-098', {
  canActivate: (card) => {
    const unit = state.playerField.find(c => c && c.pilot?.uid === card.uid);
    if (!unit) return false;
    if (effectiveHp(unit) >= effectiveMaxHp(unit)) return false; // already at max HP
    return state.oppField.some(c => c && effectiveBp(c) <= 1);
  },
  activate: (card, ctx) => {
    const unit = ctx?.unit || state.playerField.find(c => c && c.pilot?.uid === card.uid);
    if (!unit) { ctx?.onDone?.(); return; }
    applyHpMod([unit], 1);
    addLog(`${card.name} — ${unit.name} recovers 1 HP (HP: ${effectiveHp(unit)}).`);
    render();
    setupDropZones();
    ctx?.onDone?.();
  },
});

// GD01-097 "Guel Jeturk" — Pilot
// [Activate･Main][Once per Turn] If your opponent has 8 or more cards in
// their hand, set this Unit as active. It can't attack during this turn.
registerCardEffect('GD01-097', {
  canActivate: (card) => {
    const unit = state.playerField.find(c => c && c.pilot?.uid === card.uid);
    if (!unit || !unit.rested) return false;
    if (card.passiveUsedThisTurn) return false;
    if (state.oppHandCount < 8) return false;
    return true;
  },
  activate: (card, ctx) => {
    const unit = ctx.unit;
    unit.rested = false;
    unit.tempCantAttack = true;
    setRested(unit, false);
    addLog(`${card.name} — ${unit.name} is set as active. It can't attack this turn.`);
    ctx.onDone?.();
  },
});

// ─── EB01-070 · Daryl Lorenz — Pilot ──────────────────────────────────────
// [Burst] Add this card to your hand — handled generically (literal
// "[Burst]Add this card to your hand." pattern, see registry.js).
// [During Link][Activate･Action][Once per Turn]①: If it is your opponent's
// turn, choose 1 Unit. It gets AP+1 during this battle.
//
// Pilot Activate·Action abilities are offered exclusively via the Action
// Step picker (getActionEligiblePilots/resolvePilotActionActivate in
// game.js), which already sets card.passiveUsedThisTurn (the Once-per-Turn
// gate) and passes ctx = { unit, battle, onDone } to 'activate' — but
// canActivate is checked earlier against the RAW battle ctx (same
// convention as GD02-118/ST10-015), so it must look the paired unit up
// itself. RES cost isn't paid generically for Pilot actions (only Base
// Activate·Main cost is), so it's checked/spent by hand here, same as
// GD01-058/GD01-082.
// Target is restricted to the friendly Unit actually battling the enemy
// this Action Step (ctx.battle.defender while attackerSide is 'opp') per
// the card's "battling" clarification — not just any Unit on the field.
function eb01070FriendlyBattlingUnit(battle) {
  if (!battle || battle.attackerSide !== 'opp' || battle.defenderSide !== 'player') return null;
  const target = battle.defender;
  if (!target || target.isShield || target.isDirect) return null;
  if (target.type !== 'unit' && target.type !== 'unit-token') return null;
  return target;
}
registerCardEffect('EB01-070', {
  canActivate: (card, ctx) => {
    if (card.passiveUsedThisTurn) return false;
    if (state.myTurn) return false;
    if ((state.res + state.exRes) < 1) return false;
    const unit = state.playerField.find(c => c && c.pilot?.uid === card.uid);
    if (!unit || !isLinkUnit(unit)) return false;
    return !!eb01070FriendlyBattlingUnit(ctx);
  },
  activate: (card, ctx) => {
    const battle = ctx.battle || ctx;
    const target = eb01070FriendlyBattlingUnit(battle);
    if (!target) {
      addLog(`${card.name} — no friendly Unit battling the enemy. No effect.`);
      ctx.onDone?.(); return;
    }
    if ((state.res + state.exRes) < 1) {
      addLog(`${card.name} — need ① RES to activate (have ${state.res + state.exRes}).`);
      ctx.onDone?.(); return;
    }
    spendResources(1);
    broadcastCounters();
    applyApMod([target], 1, 'battleApMod');
    addLog(`${card.name} — ${target.name} gets AP+1 during this battle.`);
    render();
    setupDropZones();
    ctx.onDone?.();
  },
});

// ─── EB01-071 · Ittou Tsurugi — Pilot ─────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [During Link] This Unit gets AP+1.
// HP+1 is already handled by the pilot's `hp: +1` DB value (auto-applied by
// effectiveHp). AP+1 is granted via passiveApBonus while properly linked —
// same convention as ST02-010 Heero Yuy in green.js.
registerCardEffect('EB01-071', {
  passiveApBonus: (pilot, unit) => isLinkUnit(unit) ? 1 : 0,
});

// ─── ST10-012 · Mark Guilder — Pilot ──────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] Choose 1 enemy Unit that is Lv.5 or lower. It gets AP-2
// during this turn.
// Fires on plain Pair OR Link (no [During Link] qualifier), same convention
// as ST01-006 Gundam Aerial.
registerCardEffect('ST10-012', {
  pair: (card) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.5 or lower. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.5 or lower. It gets AP-2 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 during this turn.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── EB01-072 · Yuu Kajima — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [When Paired] Choose 1 active friendly Unit with <Blocker> and 1 enemy
// Unit that is Lv.4 or lower. Rest them.
// Fires on plain Pair OR Link (no [During Link] qualifier). Chained pickers
// (friendly then enemy) — same convention as EB01-085.
registerCardEffect('EB01-072', {
  pair: (card) => {
    const friendlies = state.playerField.filter(c => c && !c.rested && hasNativeKeyword(c, 'Blocker'));
    if (friendlies.length === 0) {
      addLog(`${card.name} — no active friendly Unit with <Blocker>. No effect.`);
      return;
    }
    const enemies = state.oppField.filter(c => c && !c.rested && (parseInt(c.level) || 0) <= 4);
    if (enemies.length === 0) {
      addLog(`${card.name} — no active enemy Units Lv.4 or lower. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 active friendly Unit with <Blocker>. Rest it.', friendlies, (friendly) => {
      if (!friendly) return;
      showCardPickerModal(card.name, 'Choose 1 active enemy Unit that is Lv.4 or lower. Rest it.', enemies, (enemy) => {
        if (!enemy) return;
        setRested(friendly, true);
        setRested(enemy, true);
        addLog(`${card.name} — rested ${friendly.name} and ${enemy.name}.`);
        render();
        setupDropZones();
      }, card, { mandatory: true });
    }, card, { mandatory: true });
  },
});

// GD02-099 "Gaelio Bauduin" — Pilot
// [When Paired] If there are 4 or more (Gjallarhorn) cards in your trash,
// choose 1 enemy Unit. It gets AP-2 during this turn.
registerCardEffect('GD02-099', {
  pair: (card, ctx) => {
    const gjallCount = state.playerTrash.filter(c =>
      (c.trait || '').includes('Gjallarhorn')
    ).length;
    if (gjallCount < 4) {
      addLog(`${card.name} — fewer than 4 (Gjallarhorn) cards in trash (${gjallCount}). No effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c && !c.isShield && !c.isDirect);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units in play. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. It gets AP-2 during this turn.',
      candidates,
      (target) => {
        if (target) {
          applyApMod([target], -2, 'tempApMod');
          addLog(`${card.name} — ${target.name} gets AP-2 during this turn.`);
          render();
          setupDropZones();
        }
      },
      card
    );
  },
});

// GD03-120 "Immortal Colasour"
// [Main] During this turn, if a friendly (Superpower Bloc)/(UN) Unit
// destroys an enemy Unit with battle damage, choose 1 rested friendly
// (Superpower Bloc)/(UN) Unit. Set it as active. It can't attack during this
// turn.
// (Full delayed-watcher implementation per the user's explicit answer when
// this was clarified — see turnDestroyWatchers/notifyUnitDestroyedByBattle
// in js/game.js. Can fire more than once this turn if the condition recurs.)
registerCardEffect('GD03-120', {
  main: (card, ctx) => {
    const traitOk = (t) => (t || '').includes('Superpower Bloc') || (t || '').includes('UN');
    turnDestroyWatchers.push((destroyer, destroyed, destroyerSide, targetType) => {
      // Card text says "an enemy Unit" specifically — unlike GD05-002 Strike
      // Freedom Gundam's "an enemy card" (see turnDestroyWatchers in
      // game.js), a shield or base kill must NOT trigger this.
      if (targetType !== 'unit') return;
      if (destroyerSide !== 'player' || !traitOk(destroyer.trait)) return;
      const candidates = state.playerField.filter(c => c && c.rested && traitOk(c.trait));
      if (candidates.length === 0) return;
      showCardPickerModal(card.name, "Choose 1 rested friendly (Superpower Bloc)/(UN) Unit. Set it as active. It can't attack during this turn.", candidates, (target) => {
        if (target) {
          setRested(target, false);
          target.tempCantAttack = true;
          addLog(`${card.name} — set ${target.name} active. It can't attack during this turn.`);
          render();
          setupDropZones();
        }
      }, card);
    });
    addLog(`${card.name} — watching for a friendly (Superpower Bloc)/(UN) Unit destroying an enemy Unit with battle damage this turn.`);
    ctx.onDone();
  },
});

// GD03-121 "Unheralded Attack"
// [Action] Choose 1 friendly Base and 1 enemy Unit with 3 or less HP. Rest
// them.
registerCardEffect('GD03-121', {
  canActivate: () => !!state.playerBase && state.oppField.some(c => c && effectiveHp(c) <= 3 && !c.rested),
  action: (card, ctx) => {
    const base = state.playerBase;
    if (!base) {
      addLog(`${card.name} — no friendly Base in play. No effect.`);
      ctx.onDone();
      return;
    }
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Unit with 3 or less HP. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, "Choose 1 enemy Unit with 3 or less HP. It and your Base are rested.", candidates, (target) => {
      if (target) {
        setRested(base, true);
        setRested(target, true);
        addLog(`${card.name} — rested ${base.name} and ${target.name}.`);
        render();
        setupDropZones();
      }
      ctx.onDone();
    }, card);
  },
});

// GD03-122 "Veteran Tactics"
// [Action] Choose 1 enemy Unit that is Lv.3 or lower. Return it to its
// owner's hand.
registerCardEffect('GD03-122', {
  canActivate: () => state.oppField.some(c => c && (parseInt(c.level) || 0) <= 3),
  action: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    pickAndBounceOne(card, candidates, "Choose 1 enemy Unit (Lv.3 or lower). Return it to its owner's hand.", ctx.onDone);
  },
});

// ─── EB01-081 · MAP Weapon ────────────────────────────────────────────────
// [Burst] Add this card to your hand — handled generically (literal
// "[Burst]Add this card to your hand." pattern, see registry.js).
// [Main]/[Action] Choose 1 to 2 enemy Units with 2 or less HP. Return them
// to their owners' hands.
function eb01081Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 2);
  pickAndBounceMulti(card, candidates, "Choose 1 to 2 enemy Units with 2 or less HP to return to their owners' hands.", { min: 1, max: 2 }, onDone);
}
registerCardEffect('EB01-081', {
  canActivate: () => state.oppField.some(c => c && effectiveHp(c) <= 2),
  main:   (card, ctx) => eb01081Effect(card, ctx.onDone),
  action: (card, ctx) => eb01081Effect(card, ctx.onDone),
});

// ─── EB01-082 · Warship Cruise ────────────────────────────────────────────
// [Burst] Activate this card's [Action].
// [Action] Choose 1 enemy Unit that is Lv.3 or lower. Return it to its
// owner's hand.
function eb01082Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
  pickAndBounceOne(card, candidates, "Choose 1 enemy Unit (Lv.3 or lower). Return it to its owner's hand.", onDone);
}
registerCardEffect('EB01-082', {
  canActivate: () => state.oppField.some(c => c && (parseInt(c.level) || 0) <= 3),
  burst:  (card, ctx) => eb01082Effect(card, ctx?.onDone ?? (() => {})),
  action: (card, ctx) => eb01082Effect(card, ctx.onDone),
});

// GD04-117 "Graceful Demeanor"
// [Burst] Activate this card's [Action].
// [Action] Choose 1 to 2 enemy Units that are Lv.3 or lower. Return them to
// their owners' hands.
function gd04117Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
  pickAndBounceMulti(card, candidates, "Choose 1 to 2 enemy Units (Lv.3 or lower) to return to their owners' hands.", { min: 1, max: 2 }, onDone);
}
registerCardEffect('GD04-117', {
  canActivate: () => state.oppField.some(c => c && (parseInt(c.level) || 0) <= 3),
  burst: (card, ctx) => gd04117Effect(card, ctx?.onDone ?? (() => {})),
  action: (card, ctx) => gd04117Effect(card, ctx.onDone),
});

// ─── EB01-052 · Hildolfr ──────────────────────────────────────────────────
// [Deploy] If 3 or more enemy Units are in play, choose 1 enemy Unit with 2
// or less HP. Return it to its owner's hand.
registerCardEffect('EB01-052', {
  deploy: (card) => {
    if (state.oppField.filter(c => c).length < 3) return;
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 2);
    pickAndBounceOne(card, candidates, "Choose 1 enemy Unit with 2 or less HP. Return it to its owner's hand.", () => {});
  },
});

// ─── EB01-046 · Striker Custom (EX) ────────────────────────────────────────
// [During Pair][Attack] Choose 1 enemy Unit that is Lv.4 or higher. It gets
// AP-2 during this battle.
registerCardEffect('EB01-046', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!card.pilot) { ctx.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) >= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.4 or higher. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.4 or higher. It gets AP-2 during this battle.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 during this battle.`);
        render();
        setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
});

// ─── EB01-047 · Casval's Gundam ────────────────────────────────────────────
// [When Paired･Development 1] You may exile 1 (G Generation) card from your
// trash (no Unit restriction — raw text says "cards", not "Unit cards"). If
// you do: This Unit gains <High-Maneuver> during this turn.
// resolveDevelopmentCost is defined in green.js but globally accessible.
registerCardEffect('EB01-047', {
  pair: (card) => {
    resolveDevelopmentCost(card, 1, () => {
      card.tempKeywords = card.tempKeywords || [];
      if (!card.tempKeywords.includes('High-Maneuver')) card.tempKeywords.push('High-Maneuver');
      NET.send({ type: 'TEMP_KEYWORD_ADD', uid: card.uid, keyword: 'High-Maneuver' });
      addLog(`${card.name} — gains <High-Maneuver> this turn.`);
      render();
    }, { unitOnly: false });
  },
});

// ─── ST10-008 · Gundam Barbatos 1st Form ──────────────────────────────────
// [Deploy･Development 2] You may exile 2 (G Generation) Unit cards from
// your trash. If you do: Draw a number of cards equal to the number of
// enemy players. Then, discard the same number of cards drawn. (1v1: draw
// 1, then discard 1.)
registerCardEffect('ST10-008', {
  deploy: (card) => {
    resolveDevelopmentCost(card, 2, () => {
      const drawn = drawCardsFromTempDeck(1, true);
      drawn.forEach(c => state.hand.push(c));
      addLog(`${card.name} — drew ${drawn.length} card${drawn.length !== 1 ? 's' : ''}.`);
      render();
      setupDropZones();
      showHandDiscardModal(() => {
        broadcastCounters();
      }, {
        needed: 1,
        exact: true,
        title: card.name,
        question: 'Select 1 card to discard.',
      });
    });
  },
});

// ─── EB01-057 · Gundam Geminass 02 ─────────────────────────────────────────
// [Deploy] You may choose 1 active friendly Unit that is Lv.3. Rest it. If
// you do, choose 1 enemy Unit that is Lv.2 or lower. Return it to its
// owner's hand.
registerCardEffect('EB01-057', {
  deploy: (card) => {
    const friendlies = state.playerField.filter(c => c && !c.rested && (parseInt(c.level) || 0) === 3);
    if (friendlies.length === 0) return;
    showCardPickerModal(card.name, 'You may choose 1 active friendly Unit that is Lv.3. Rest it.', friendlies, (friendly) => {
      if (!friendly) return;
      setRested(friendly, true);
      addLog(`${card.name} — rested ${friendly.name}.`);
      const enemies = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 2);
      if (enemies.length === 0) {
        addLog(`${card.name} — no enemy Units Lv.2 or lower. No effect.`);
        render();
        setupDropZones();
        return;
      }
      showCardPickerModal(card.name, "Choose 1 enemy Unit that is Lv.2 or lower. Return it to its owner's hand.", enemies, (enemy) => {
        if (enemy) {
          bounceEnemyUnit(enemy);
          addLog(`${card.name} — returned ${enemy.name} to its owner's hand.`);
        }
        render();
        setupDropZones();
      }, card, { mandatory: true });
    }, card, { mandatory: false });
  },
});

// ─── EB01-043 · Blue Destiny Unit-1 (EX) ───────────────────────────────────
// [Attack] If a friendly Unit with <Blocker> is in play, choose 1 enemy
// Unit that is Lv.5 or lower. It gets AP-2 during this battle.
registerCardEffect('EB01-043', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!state.playerField.some(c => c && hasNativeKeyword(c, 'Blocker'))) {
      addLog(`${card.name} — no friendly Unit with <Blocker> in play. No effect.`);
      ctx.onDone?.(); return;
    }
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.5 or lower. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.5 or lower. It gets AP-2 during this battle.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 during this battle.`);
        render();
        setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
});

// ─── EB01-060 · Gundam Aquarius ────────────────────────────────────────────
// [When Paired･Development 3] You may exile 3 (G Generation) cards from
// your trash (no Unit restriction — raw text says "cards", not "Unit
// cards"). If you do: Choose 1 enemy Unit that is Lv.4 or lower. Return it
// to its owner's hand.
// Fires on plain Pair OR Link (no [During Link] qualifier). Note: this is a
// [When Paired] trigger, not [Deploy].
registerCardEffect('EB01-060', {
  pair: (card) => {
    resolveDevelopmentCost(card, 3, () => {
      const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
      if (candidates.length === 0) {
        addLog(`${card.name} — no enemy Units Lv.4 or lower. No effect.`);
        return;
      }
      showCardPickerModal(card.name, "Choose 1 enemy Unit that is Lv.4 or lower. Return it to its owner's hand.", candidates, (target) => {
        if (target) {
          bounceEnemyUnit(target);
          addLog(`${card.name} — returned ${target.name} to its owner's hand.`);
          render();
          setupDropZones();
        }
      }, card, { mandatory: true });
    }, { unitOnly: false });
  },
});

// ─── ST10-006 · Phoenix Gundam (Power Unleashed) (EX) ─────────────────────
// [During Pair] During your turn, when this Unit destroys an enemy Unit
// with battle damage, choose 1 enemy Unit with 3 or less HP. Return it to
// its owner's hand.
// Same destroyerIsAttacker/destroyerSide/myTurn gating convention as
// ST07-005 Gundam Dynames (identical underlying text pattern), plus the
// [During Pair] gate on card.pilot.
registerCardEffect('ST10-006', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide, destroyerIsAttacker) => {
    if (!card.pilot) return;
    if (destroyerSide !== 'player' || !destroyerIsAttacker || !state.myTurn) return;
    const candidates = state.oppField.filter(c => c && !c.isShield && !c.isDirect && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 3 or less HP.`);
      return;
    }
    showCardPickerModal(card.name, "Choose 1 enemy Unit with 3 or less HP. Return it to its owner's hand.", candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(`${card.name} — returned ${target.name} to its owner's hand.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── EB01-059 · Psycho Zaku ────────────────────────────────────────────────
// [During Link][Attack][Once per Turn] All players each choose 1 of their
// Resources. Set them as active.
// "Set 1 Resource as active" = state.res = min(res+1, resBase), same
// mechanism as ST07-001/GD04-something's rested-resource activation.
// "All players" mirrors to the opponent's own state via
// FORCE_SET_RESOURCE_ACTIVE (network.js), same "each side handles their own
// state" convention as FORCE_DRAW/FORCE_PLACE_EX_RESOURCE.
registerCardEffect('EB01-059', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(card)) { ctx.onDone?.(); return; }
    if (card.passiveUsedThisTurn) { ctx.onDone?.(); return; }
    card.passiveUsedThisTurn = true;
    state.res = Math.min(state.res + 1, state.resBase);
    state.resourceSetActiveThisTurn = true;
    addLog(`${card.name} — set 1 Resource as active (RES now ${state.res}).`);
    broadcastCounters();
    render();
    if (state.networked) NET.send({ type: 'FORCE_SET_RESOURCE_ACTIVE' });
    ctx.onDone?.();
  },
});

// ─── ST10-007 · Gundam Barbatos 4th Form ───────────────────────────────────
// [When Linked･Development 2] You may exile 2 (G Generation) Unit cards
// from your trash. If you do: Choose 1 Command card that is Lv.4 or lower
// from your trash. Add it to your hand.
registerCardEffect('ST10-007', {
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    resolveDevelopmentCost(card, 2, () => {
      const candidates = state.playerTrash.filter(c =>
        (c.type === 'command' || c.type === 'command pilot') && (parseInt(c.level) || 0) <= 4
      );
      if (candidates.length === 0) {
        addLog(`${card.name} — no Command cards Lv.4 or lower in trash. No effect.`);
        return;
      }
      showCardPickerModal(card.name, 'Choose 1 Command card that is Lv.4 or lower from your trash. Add it to your hand.', candidates, (target) => {
        if (!target) return;
        const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
        if (idx !== -1) {
          state.playerTrash.splice(idx, 1);
          state.trashCount = Math.max(0, state.trashCount - 1);
        }
        updateTempState(target.cardNo, target.copyVer, 'hand');
        state.hand.push(target);
        addLog(`${card.name} — added ${target.name} from trash to hand.`);
        broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
        glowHandCard(target.uid);
        broadcastCounters();
        render();
        setupDropZones();
      }, card, { mandatory: true });
    });
  },
});

// ─── EB01-049 · Pale Rider (Ground Heavy Equipment Type) ──────────────────
// While a friendly (G Generation) Unit with <Blocker> is in play, this Unit
// gains <Suppression>.
registerCardEffect('EB01-049', {
  passiveKeywords: (card, ownerSide) => {
    const field = ownerSide === 'player' ? state.playerField : state.oppField;
    const hasGGenBlocker = field.some(c =>
      c && (c.trait || '').includes('(G Generation)') && hasNativeKeyword(c, 'Blocker')
    );
    return hasGGenBlocker ? ['Suppression'] : [];
  },
});

// ─── EB01-050 · Saikoro Gundam ──────────────────────────────────────────────
// [Attack] Place the top card of your deck into your trash. If you placed
// a card that is Lv.3 or higher with this effect, choose 1 enemy Unit. It
// gets AP-2 during this battle.
registerCardEffect('EB01-050', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const top = peekTopOfDeck(1)[0];
    if (!top) {
      addLog(`${card.name} — deck is empty, nothing to mill.`);
      ctx.onDone?.(); return;
    }
    const { entry, card: milled } = top;
    entry.state = 'trash';
    state.trashCount++;
    state.playerTrash.push(toTrashCard(milled));
    state.deckCount = state.tempDeck.filter(isInDeck).length;
    NET.send({ type: 'DECK_MILL', card: milled });
    broadcastCardToast(`${milled.name} was sent to trash.`);
    addLog(`${card.name} — milled ${milled.name} to trash.`);
    broadcastCounters();
    render();
    setupDropZones();
    if ((parseInt(milled.level) || 0) < 3) { ctx.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units to target.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-2 during this battle.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 during this battle.`);
        render();
        setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
});

// ─── EB01-045 · Psycho Zaku (EX) ────────────────────────────────────────────
// <Suppression> (native, handled generically).
// [When Paired] Choose 1 enemy Unit with <Repair>. Return it to its owner's
// hand.
// Uses hasKwIcon (prefix match) rather than hasNativeKeyword (exact bracket
// match) so numbered variants like <Repair 2> also qualify.
registerCardEffect('EB01-045', {
  pair: (card) => {
    const candidates = state.oppField.filter(c => c && hasKwIcon(c, 'Repair'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with <Repair>. No effect.`);
      return;
    }
    showCardPickerModal(card.name, "Choose 1 enemy Unit with <Repair>. Return it to its owner's hand.", candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(`${card.name} — returned ${target.name} to its owner's hand.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── EB01-041 · Strike Freedom Gundam (EX) ─────────────────────────────────
// <High-Maneuver> (native, handled generically).
// [Deploy] Choose 1 Unit with 4 or less HP belonging to each enemy player.
// Return them to their owners' hands. (1v1: just the single opponent.)
registerCardEffect('EB01-041', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 4);
    pickAndBounceOne(card, candidates, "Choose 1 enemy Unit with 4 or less HP. Return it to its owner's hand.", () => {});
  },
});

// ─── EB01-042 · Psycho Haro (EX) ────────────────────────────────────────────
// While this Unit is rested, all Units gain <Blocker>.
// [Attack] Units that are Lv.7 or lower can't activate <Blocker> during
// this battle.
// First half uses the new globalKeywordAllSides hook (checked in
// hasNativeKeyword, scans BOTH fields — unlike globalUnitKeyword, which is
// same-side only). Second half uses the new cantBlockLevelCeiling hook
// (checked in runBlockStep), same convention as the existing
// <High-Maneuver>-blocks-all-blockers check.
registerCardEffect('EB01-042', {
  globalKeywordAllSides: (_target, sourceCard) => sourceCard.rested ? ['Blocker'] : [],
  cantBlockLevelCeiling: () => 7,
});

// GD04-118 "World Distortion"
// [Main]/[Action] If 2 or more friendly (UN) Units are in play, choose 1
// enemy Unit with 5 or less HP. Return it to its owner's hand.
function gd04118Effect(card, onDone) {
  const unCount = state.playerField.filter(c => c && (c.trait || '').includes('UN')).length;
  if (unCount < 2) {
    addLog(`${card.name} — fewer than 2 friendly (UN) Units in play. No effect.`);
    onDone();
    return;
  }
  const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 5);
  pickAndBounceOne(card, candidates, "Choose 1 enemy Unit with 5 or less HP. Return it to its owner's hand.", onDone);
}
registerCardEffect('GD04-118', {
  canActivate: () => {
    const unCount = state.playerField.filter(c => c && (c.trait || '').includes('UN')).length;
    return unCount >= 2 && state.oppField.some(c => c && effectiveHp(c) <= 5);
  },
  main: (card, ctx) => gd04118Effect(card, ctx.onDone),
  action: (card, ctx) => gd04118Effect(card, ctx.onDone),
});

// GD04-119 "Fighting Alone"
// [Main]/[Action] Choose 1 friendly Unit paired with a (Newtype) Pilot. It
// can't receive effect damage from enemy Units during this turn.
// (No "effect damage to a Unit" source exists anywhere in this codebase yet
// — Breach's bonus damage only ever targets a Base/shield, never a Unit —
// so this sets card.tempCantReceiveEffectDamage as future-proofing for if
// such a mechanic is ever added; it has no observable effect today. Cleared
// on UNREST_ALL alongside the other "during this turn" temp flags.)
function gd04119Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && c.pilot && (c.pilot.trait || '').includes('Newtype'));
  if (candidates.length === 0) {
    addLog(`${card.name} — no friendly Unit paired with a (Newtype) Pilot. No effect.`);
    onDone();
    return;
  }
  showCardPickerModal(card.name, "Choose 1 friendly Unit paired with a (Newtype) Pilot. It can't receive effect damage from enemy Units during this turn.", candidates, (target) => {
    if (target) {
      target.tempCantReceiveEffectDamage = true;
      addLog(`${card.name} — ${target.name} can't receive effect damage from enemy Units during this turn.`);
      render();
      setupDropZones();
    }
    onDone();
  }, card);
}
registerCardEffect('GD04-119', {
  canActivate: () => state.playerField.some(c => c && c.pilot && (c.pilot.trait || '').includes('Newtype')),
  main: (card, ctx) => gd04119Effect(card, ctx.onDone),
  action: (card, ctx) => gd04119Effect(card, ctx.onDone),
});

// GD04-120 "Machine Doll Squad"
// [Main]/[Action] Choose 1 friendly (Militia)/(Dianna Counter) Unit. It gets
// AP+2 during this turn.
function gd04120Effect(card, onDone) {
  const candidates = state.playerField.filter(c => c && ((c.trait || '').includes('Militia') || (c.trait || '').includes('Dianna Counter')));
  pickAndApplyApMod(card, candidates, 2, 'tempApMod', "Choose 1 friendly (Militia)/(Dianna Counter) Unit. It gets AP+2 during this turn.", onDone);
}
registerCardEffect('GD04-120', {
  canActivate: () => state.playerField.some(c => c && ((c.trait || '').includes('Militia') || (c.trait || '').includes('Dianna Counter'))),
  main: (card, ctx) => gd04120Effect(card, ctx.onDone),
  action: (card, ctx) => gd04120Effect(card, ctx.onDone),
});

// ST01-014 "Unforeseen Incident"
// [Burst] Activate this card's [Main].
// [Main]/[Action] Choose 1 enemy Unit. It gets AP-3 during this turn.
function st01014Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c);
  pickAndApplyApMod(card, candidates, -3, 'tempApMod', "Choose 1 enemy Unit. It gets AP-3 during this turn.", onDone);
}
registerCardEffect('ST01-014', {
  canActivate: () => state.oppField.some(c => c),
  burst: (card, ctx) => st01014Effect(card, ctx?.onDone ?? (() => {})),
  main: (card, ctx) => st01014Effect(card, ctx.onDone),
  action: (card, ctx) => st01014Effect(card, ctx.onDone),
});

// ST04-001 "Aile Strike Gundam" — Unit
// <Blocker> — native keyword.
// [When Paired･Lv.4 or Higher Pilot] Choose 1 enemy Unit with 4 or less HP.
// Return it to its owner's hand.
// The pilot's level is checked on card.pilot (already attached at trigger time).
registerCardEffect('ST04-001', {
  pair: (card) => {
    const pilot = card.pilot;
    if (!pilot || (parseInt(pilot.level) || 0) < 4) return;
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 4 or less HP.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 4 or less HP. Return it to its owner\'s hand.', candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(isUnitToken(target)
          ? `${card.name} — ${target.name} (token) removed from the game.`
          : `${card.name} — ${target.name} returned to opponent's hand.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ST04-002 "Strike Gundam" — Unit
// [Deploy] Draw 1. Then, discard 1.
registerCardEffect('ST04-002', {
  deploy: (card) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length === 0) {
      addLog(`${card.name} — deck empty, no draw. No discard required.`);
      broadcastCounters(); render(); return;
    }
    state.hand.push(drawn[0]);
    addLog(`${card.name} — drew ${drawn[0].name}.`);
    broadcastCounters(); render();
    showCardPickerModal(card.name, 'Choose 1 card from your hand to discard.', [...state.hand], (target) => {
      if (target) {
        state.hand = state.hand.filter(c => c.uid !== target.uid);
        updateTempState(target.cardNo, target.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(target));
        broadcastLog(`${card.name} — discarded ${target.name}.`);
        broadcastCardToast(`${target.name} was discarded due to ${card.name} effect.`);
      }
      broadcastCounters(); render();
    }, card, { mandatory: true });
  },
});

// ST04-012 "Striker Pack"
// [Burst] If you have no (Earth Alliance) Unit tokens in play, deploy 1
// [Aile Strike Gundam] ((Earth Alliance)･AP3･HP3･<Blocker>) Unit token.
// [Main] If you have no (Earth Alliance) Unit tokens in play, deploy 1
// [Sword Strike Gundam] or 1 [Launcher Strike Gundam] Unit token.
function hasEarthAllianceUnitToken() {
  return state.playerField.some(c => c && c.type === 'unit-token' && (c.trait || '').includes('Earth Alliance'));
}
registerCardEffect('ST04-012', {
  canActivate: () => !hasEarthAllianceUnitToken(),
  burst: (card) => {
    if (hasEarthAllianceUnitToken()) return;
    deployTokenToField('T-008');
  },
  main: (card, ctx) => {
    if (hasEarthAllianceUnitToken()) {
      addLog(`${card.name} — an (Earth Alliance) Unit token is already in play. No effect.`);
      ctx.onDone();
      return;
    }
    const options = ['T-010', 'T-009'].map(no => cardDb[no]).filter(Boolean).map(entry => buildGameCardFromDb(entry, 'preview'));
    if (options.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 Unit token to deploy: Sword Strike Gundam (AP4/HP2) or Launcher Strike Gundam (AP2/HP4).', options, (picked) => {
      if (picked) deployTokenToField(picked.cardNo);
      ctx.onDone();
    }, card);
  },
});

// ST04-013 "Hawk of Endymion"
// [Main]/[Action] Choose 1 enemy Unit with 3 or less HP. Return it to its
// owner's hand.
function st04013Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 3);
  pickAndBounceOne(card, candidates, "Choose 1 enemy Unit with 3 or less HP. Return it to its owner's hand.", onDone);
}
registerCardEffect('ST04-013', {
  canActivate: () => state.oppField.some(c => c && effectiveHp(c) <= 3),
  main: (card, ctx) => st04013Effect(card, ctx.onDone),
  action: (card, ctx) => st04013Effect(card, ctx.onDone),
});

// ── Base cards ──
// ("[Burst]Deploy this card." and "[Deploy]Add 1 of your Shields to your
// hand." are both already generic — deployBaseFromShield()/
// applyBaseDeployEffect() in js/game.js — so handlers below only cover each
// base's effect text beyond that boilerplate.)

// ST04-015 "Archangel"
// [Activate･Main][Once per Turn]②: Choose 1 friendly Unit with <Blocker>.
// Set it as active. It can't attack during this turn.
registerCardEffect('ST04-015', {
  canActivate: () => state.playerField.some(c => c && c.rested && hasNativeKeyword(c, 'Blocker')),
  activate: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && c.rested && hasNativeKeyword(c, 'Blocker'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested friendly Unit with <Blocker>. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, "Choose 1 friendly Unit with <Blocker>. Set it as active. It can't attack during this turn.", candidates, (target) => {
      if (target) {
        setRested(target, false);
        target.tempCantAttack = true;
        addLog(`${card.name} — set ${target.name} active. It can't attack during this turn.`);
        render();
        setupDropZones();
      }
      ctx.onDone();
    }, card);
  },
});

// ST01-016 "Asticassia School of Technology, Earth House"
// [Activate･Main]Rest this Base: All friendly Link Units get AP+1 during
// this turn.
registerCardEffect('ST01-016', {
  canActivate: () => state.playerField.some(c => c && isLinkUnit(c)),
  activate: (card, ctx) => {
    const targets = state.playerField.filter(c => c && isLinkUnit(c));
    if (targets.length === 0) {
      addLog(`${card.name} — no friendly Link Units. No effect.`);
      ctx.onDone();
      return;
    }
    applyApMod(targets, 1, 'tempApMod');
    addLog(`${card.name} — all friendly Link Units get AP+1 during this turn.`);
    render();
    setupDropZones();
    ctx.onDone();
  },
});

// GD01-129 "Kusanagi"
// [Deploy] ...Then, choose 1 enemy Unit with 3 or less HP. Return it to its
// owner's hand.
registerCardEffect('GD01-129', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 3);
    pickAndBounceOne(card, candidates, "Choose 1 enemy Unit with 3 or less HP. Return it to its owner's hand.", () => {});
  },
});

// GD01-065 "Freedom Gundam" — Unit
// <Blocker> native.
// [During Pair][Once per Turn] When you pair a Pilot with THIS Unit OR with
// any other friendly white Unit (while this Unit already has a Pilot): choose
// 1 enemy Unit → AP-2 this turn.
// passiveUsedThisTurn tracks the "once per turn" limit (cleared by UNREST_ALL).
function gd01065TryEffect(card) {
  if (card.passiveUsedThisTurn) return; // already fired this turn
  const candidates = state.oppField.filter(c => c);
  if (candidates.length === 0) { addLog(`${card.name} — no enemy Units to target.`); return; }
  card.passiveUsedThisTurn = true;
  showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-2 this turn.', candidates, (target) => {
    if (target) {
      applyApMod([target], -2, 'tempApMod');
      addLog(`${card.name} — ${target.name} gets AP-2 this turn.`);
      render();
    }
  }, card, { mandatory: true });
}

registerCardEffect('GD01-065', {
  // Fires when GD01-065 itself is paired with a pilot.
  pair: (card) => gd01065TryEffect(card),
  // Fires when any other field unit is paired — only triggers if this unit
  // already has a pilot and the newly-paired unit is white.
  onAnyUnitPair: (card, pairedUnit, pairedColor) => {
    if (!card.pilot) return;
    if (pairedColor !== 'white') return;
    gd01065TryEffect(card);
  },
});

// GD01-066 "Justice Gundam" — Unit
// [Deploy] Deploy 1 Fatum-00 (T-011) Unit token; it cannot attack the turn
// it was deployed (tempCantAttack = true).
// [During Pair][Attack] Choose 1 (Triple Ship Alliance) Unit token that can't
// attack this turn → clear tempCantAttack so it may attack.
registerCardEffect('GD01-066', {
  deploy: (card) => {
    const token = deployTokenToField('T-011');
    if (!token) { addLog(`${card.name} — no empty field slot for Fatum-00 token.`); return; }
    addLog(`${card.name} — Fatum-00 token deployed (cannot attack this turn without Justice Gundam's effect).`);
    render();
  },
  attack: (card, ctx) => {
    if (!card.pilot) { ctx.onDone(); return; } // [During Pair]
    const candidates = state.playerField.filter(c =>
      c && c.type === 'unit-token' &&
      (c.trait || '').includes('(Triple Ship Alliance)') &&
      c.tempCantAttack
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no restricted (Triple Ship Alliance) Unit tokens on field.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 (Triple Ship Alliance) Unit token. It may attack this turn.', candidates, (target) => {
      if (target) {
        target.tempCantAttack = false;
        addLog(`${card.name} — ${target.name} may now attack this turn.`);
        render();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// GD01-067 "Gundam Aerial Rebuild" — Unit
// [When Paired] Choose 1 Command (or Command Pilot) card Lv.5 or lower from
// your trash. Add it to your hand.
registerCardEffect('GD01-067', {
  pair: (card) => {
    const candidates = state.playerTrash.filter(c =>
      (c.type === 'command' || c.type === 'command pilot') &&
      (parseInt(c.level) || 0) <= 5
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no Command cards Lv.5 or lower in trash.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 Command card Lv.5 or lower from your trash. Add it to your hand.', candidates, (target) => {
      if (!target) return;
      const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
      if (idx !== -1) {
        state.playerTrash.splice(idx, 1);
        state.trashCount = Math.max(0, state.trashCount - 1);
      }
      updateTempState(target.cardNo, target.copyVer, 'hand');
      state.hand.push(target);
      addLog(`${card.name} — ${target.name} recovered from trash to hand.`);
      broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
      broadcastCounters(); render();
    }, card, { mandatory: true });
  },
});

// GD01-068 "Perfect Strike Gundam" — Unit
// <Blocker> native. [Deploy] Choose 1 enemy Unit with 1 HP. Return it to hand.
registerCardEffect('GD01-068', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 1);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 1 HP.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 1 HP. Return it to its owner\'s hand.', candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(`${card.name} — ${target.name} returned to opponent's hand.`);
        render();
      }
    }, card, { mandatory: true });
  },
});

// GD01-069 "Strike Rouge" — Unit
// [Activate･Main][Once per Turn]①: Choose 1 of your rested white Units with
// <Blocker>. Set it as active. It can't attack during this turn.
// Cost/once-per-turn gate handled by showUnitActivateModal/canActivateUnitEffect.
// canActivate gates the activate button so it only appears when a valid target
// is on the field (prevents paying RES with no rested Blocker to unrest).
// Token Units (e.g. T-008/T-009/T-010 Aile/Launcher/Sword Strike Gundam) are
// white/Earth Alliance in theme, but every token in the database carries
// color:"resource" (tokens aren't deck-buildable, so they were never given a
// real deck color) — a strict color==='white' check silently excluded every
// Blocker token from ever qualifying. Tokens bypass the color check
// entirely here; non-token Units still must be printed white, matching the
// card's literal text.
const gd01069Candidate = c => c && c.rested && hasNativeKeyword(c, 'Blocker') && (isUnitToken(c) || (c.color || '') === 'white');
registerCardEffect('GD01-069', {
  canActivate: () => state.playerField.some(gd01069Candidate),
  activate: (card, ctx) => {
    const candidates = state.playerField.filter(gd01069Candidate);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested white Blocker Units to set active.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name,
      'Choose 1 of your rested white Blocker Units. Set it as active. It cannot attack this turn.',
      candidates, (target) => {
        if (!target) { ctx.onDone(); return; }
        setRested(target, false);
        target.tempCantAttack = true;
        addLog(`${card.name} — ${target.name} set to active. It cannot attack this turn.`);
        render();
        ctx.onDone();
      }, card);
  },
});

// GD01-074 "Chuchu's Demi Trainer" — Unit
// [Attack] Draw 1. Then, discard 1.
// Fires as a unit attack trigger (fireAttackerUnitAttackTrigger) — before the
// Block Step. ctx.onDone MUST be called after the full flow so the battle
// pipeline can continue. If the deck is empty, skip the discard and proceed.
registerCardEffect('GD01-074', {
  attack: (card, ctx) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length === 0) {
      addLog(`${card.name} — deck empty, no draw. No discard required.`);
      broadcastCounters();
      render();
      ctx.onDone();
      return;
    }
    state.hand.push(drawn[0]);
    addLog(`${card.name} — drew ${drawn[0].name}.`);
    broadcastCounters();
    render();

    showCardPickerModal(
      card.name,
      'Choose 1 card from your hand to discard.',
      [...state.hand],
      (target) => {
        if (target) {
          state.hand = state.hand.filter(c => c.uid !== target.uid);
          updateTempState(target.cardNo, target.copyVer, 'trash');
          state.trashCount++;
          state.playerTrash.push(toTrashCard(target));
          broadcastLog(`${card.name} — discarded ${target.name}.`);
          broadcastCardToast(`${target.name} was discarded due to ${card.name} effect.`);
        }
        broadcastCounters();
        render();
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// GD01-070 "Gundam Aerial" — Unit
// Passive (hand): while 4 or more Command cards in trash, cost -2 (3 → 1).
// effectiveHandCost() clamps to 0 minimum.
registerCardEffect('GD01-070', {
  handCostReduction: () =>
    state.playerTrash.filter(c => c.type === 'command' || c.type === 'command pilot').length >= 4 ? 2 : 0,
});

// GD01-071 "Gundam Pharact" — Unit
// [During Link][Attack] Choose 1 enemy Unit. It gets AP-2 during this battle.
// [During Link] condition checked here — if the unit isn't linked with its
// pilot the effect is skipped and onDone advances the attack sequence normally.
registerCardEffect('GD01-071', {
  attack: (card, ctx) => {
    if (!isLinkUnit(card)) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units to target.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-2 during this battle.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 this battle.`);
        render();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// GD01-082 "Gundam Aerial (Mirasoul Flight Unit)" — Unit
// [During Pair][Activate･Action][Once per Turn]②:
// Choose 1 enemy Unit. It gets AP-1 during this battle.
// activateActionUsedThisTurn gate set by resolveUnitActionActivate; cleared at
// start of next turn by UNREST_ALL.
registerCardEffect('GD01-082', {
  canAction: (card) => !!(card.pilot),
  action: (card, ctx) => {
    if ((state.res + state.exRes) < 2) {
      addLog(`${card.name} — need ② RES to activate (have ${state.res + state.exRes}).`);
      ctx.onDone();
      return true;
    }
    spendResources(2);
    broadcastCounters();
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units to target.`);
      render();
      ctx.onDone();
      return true;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-1 during this battle.', candidates, (target) => {
      if (target) {
        applyApMod([target], -1, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP-1 this battle.`);
      }
      render();
      ctx.onDone();
    }, card);
    return true;
  },
});

// GD01-076 "Michaelis" — Unit
// Passive: while 4 or more Command cards are in the owner's trash, this Unit
// gets AP+1 and HP+1. Both bonuses are evaluated live each AP/HP read so they
// appear and disappear instantly as cards enter or leave the trash.
registerCardEffect('GD01-076', {
  passiveUnitApBonus: (card, ownerSide) =>
    state[`${ownerSide}Trash`].filter(c => c.type === 'command' || c.type === 'command pilot').length >= 4 ? 1 : 0,
  passiveUnitHpBonus: (card, ownerSide) =>
    state[`${ownerSide}Trash`].filter(c => c.type === 'command' || c.type === 'command pilot').length >= 4 ? 1 : 0,
});

// GD01-073 "Sword Strike Gundam" — Unit
// [During Link][Attack] Choose 1 enemy Unit with 2 or less HP.
// Return it to its owner's hand.
registerCardEffect('GD01-073', {
  attack: (card, ctx) => {
    if (!isLinkUnit(card)) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 2);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 2 or less HP.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 2 or less HP. Return it to its owner\'s hand.', candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(isUnitToken(target)
          ? `${card.name} — ${target.name} (token) removed from the game.`
          : `${card.name} — ${target.name} returned to opponent's hand.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// GD01-075 "Darilbalde" — Unit
// [Deploy] Choose 1 enemy Unit with 1 HP. Return it to its owner's hand.
registerCardEffect('GD01-075', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && effectiveHp(c) === 1);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 1 HP.`); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 1 HP. Return it to its owner\'s hand.',
      candidates, (target) => {
        if (!target) return;
        bounceEnemyUnit(target);
        addLog(isUnitToken(target)
          ? `${card.name} — ${target.name} (token) removed from the game.`
          : `${card.name} — returned ${target.name} to its owner's hand.`);
        render(); setupDropZones();
      }, card);
  },
});

// ─── EB01-090 · Tiffa Adill & Freeden ────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Then, if it is your turn, choose 1 enemy Unit with 2 or less HP. Return it
// to its owner's hand.
registerCardEffect('EB01-090', {
  deploy: (card) => {
    if (!state.myTurn) return;
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 2);
    pickAndBounceOne(card, candidates, "Choose 1 enemy Unit with 2 or less HP. Return it to its owner's hand.", () => {});
  },
});

// ─── EB01-089 · Lacus Clyne & Eternal ────────────────────────────────────
// [Deploy] Add 1 Shield to hand — handled generically by applyBaseDeployEffect().
// Then, choose 1 rested friendly white (G Generation) Unit. Set it as
// active. It can't attack during this turn.
registerCardEffect('EB01-089', {
  deploy: (card) => {
    const candidates = state.playerField.filter(c => c && c.rested && (c.color || '') === 'white' && (c.trait || '').includes('(G Generation)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested white (G Generation) Units to set active.`);
      return;
    }
    showCardPickerModal(card.name,
      "Choose 1 rested friendly white (G Generation) Unit. Set it as active. It can't attack during this turn.",
      candidates, (target) => {
        if (!target) return;
        setRested(target, false);
        target.tempCantAttack = true;
        addLog(`${card.name} — ${target.name} set to active. It cannot attack this turn.`);
        render();
        setupDropZones();
      }, card);
  },
});

// GD01-078 "Mistral" — Unit
// [Deploy] Choose 1 enemy Unit. It gets AP-1 during this turn.
registerCardEffect('GD01-078', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c);
    pickAndApplyApMod(card, candidates, -1, 'tempApMod', "Choose 1 enemy Unit. It gets AP-1 during this turn.", () => {});
  },
});

// GD01-081 "M1 Astray" — Unit
// Passive: while another (Triple Ship Alliance) Unit is in play (excluding
// itself), this Unit gets AP+1 and <Blocker>.
// Both hooks are evaluated live on every AP/keyword read — no turn state needed.
function gd01081HasTSA(card, ownerSide) {
  return state[`${ownerSide}Field`].some(c => c && c.uid !== card.uid && (c.trait || '').includes('Triple Ship Alliance'));
}
registerCardEffect('GD01-081', {
  passiveUnitApBonus: (card, ownerSide) => gd01081HasTSA(card, ownerSide) ? 1 : 0,
  passiveKeywords:    (card, ownerSide) => gd01081HasTSA(card, ownerSide) ? ['Blocker'] : [],
});

// GD01-080 "Cagalli's Skygrasper" — Unit
// [Destroyed] Choose 1 enemy Unit that is Lv.2 or lower. Return it to its
// owner's hand. Fires after all battle steps complete (end of runDamageStep).
// ctx has no ownerSide for unit triggers — derive it from which side the
// destroyed card belonged to, so the picker only opens on the controller's
// client.
registerCardEffect('GD01-080', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (ownerSide !== 'player') return;
    const candidates = state.oppField.filter(c => c && !c.isShield && !c.isDirect && (parseInt(c.level) || 0) <= 2);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Unit (Lv.2 or lower) to return.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit that is Lv.2 or lower. Return it to its owner\'s hand.',
      candidates,
      (target) => {
        if (target) {
          bounceEnemyUnit(target);
          addLog(isUnitToken(target)
            ? `${card.name} — ${target.name} (token) removed from the game.`
            : `${card.name} — returned ${target.name} to its owner's hand.`);
          render();
          setupDropZones();
        }
      },
      card
    );
  },
});

// GD01-130 "13th Tactical Testing Sector"
// [Activate･Main]Rest this Base: If a friendly (Academy) Unit is in play,
// choose 1 enemy Unit. It gets AP-1 during this turn.
registerCardEffect('GD01-130', {
  canActivate: () => state.playerField.some(c => c && (c.trait || '').includes('Academy')),
  activate: (card, ctx) => {
    const hasAcademy = state.playerField.some(c => c && (c.trait || '').includes('Academy'));
    if (!hasAcademy) {
      addLog(`${card.name} — no friendly (Academy) Unit in play. No effect.`);
      ctx.onDone();
      return;
    }
    const candidates = state.oppField.filter(c => c);
    pickAndApplyApMod(card, candidates, -1, 'tempApMod', "Choose 1 enemy Unit. It gets AP-1 during this turn.", ctx.onDone);
  },
});

// GD02-130 "Sleipnir"
// [Deploy] ...Then, if a friendly (Gjallarhorn) Unit is in play, choose 1
// enemy Unit. It gets AP-2 during this turn.
registerCardEffect('GD02-130', {
  deploy: (card) => {
    const hasGjallarhorn = state.playerField.some(c => c && (c.trait || '').includes('Gjallarhorn'));
    if (!hasGjallarhorn) {
      addLog(`${card.name} — no friendly (Gjallarhorn) Unit in play. No effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c);
    pickAndApplyApMod(card, candidates, -2, 'tempApMod', "Choose 1 enemy Unit. It gets AP-2 during this turn.", () => {});
  },
});

// GD03-069 "Graham's Union Flag Custom" — Unit
// <High-Maneuver> — handled natively (keyword in DB; blocker step already
// skips eligibles when attacker has High-Maneuver, see line 4764 in game.js).
// [During Link] End of turn: if this unit was linked with its pilot THIS turn
// and it is rested, set it as active.
// linkedThisTurn is set in PAIR_PILOT when a new pilot attaches to a link unit;
// cleared by CLEAR_TURN_EFFECTS at end of same turn.
registerCardEffect('GD03-069', {
  endOfTurnLinked: (card) => {
    if (!card.rested) return;
    setRested(card, false);
    addLog(`${card.name} — set active (linked with pilot this turn).`);
    render();
  },
});

// GD03-070 "Freedom Gundam" — Unit
// Passive: while this Unit is rested, friendly Shields cannot receive battle
// damage from enemy Units (attacker's AP is treated as 0 vs shields).
registerCardEffect('GD03-070', {
  grantsShieldImmunity: (card) => !!card.rested,
});

// GD03-071 "Z Gundam (Biosensor)" — Unit
// [Deploy] Choose 1 enemy Unit. For each (AEUG) Unit in player trash, it gets AP-1 this turn.
registerCardEffect('GD03-071', {
  deploy: (card) => {
    const aeugCount = state.playerTrash.filter(c =>
      c.type === 'unit' && (c.trait || '').includes('(AEUG)')
    ).length;
    if (aeugCount === 0) {
      addLog(`${card.name} — no (AEUG) Units in trash. No effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units on field. No effect.`);
      return;
    }
    showCardPickerModal(card.name, `Choose 1 enemy Unit. It gets AP-${aeugCount} this turn (${aeugCount} (AEUG) Unit${aeugCount !== 1 ? 's' : ''} in trash).`, candidates, (target) => {
      if (target) {
        applyApMod([target], -aeugCount, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-${aeugCount} this turn.`);
        render();
      }
    }, card, { mandatory: true });
  },
});

// GD03-072 "Aile Strike Gundam" — Unit
// [Deploy] If you have another (Triple Ship Alliance) Unit in play, draw 1.
// Then, discard 1.
registerCardEffect('GD03-072', {
  deploy: (card) => {
    const hasTSA = state.playerField.some(c => c && c.uid !== card.uid && (c.trait || '').includes('Triple Ship Alliance'));
    if (!hasTSA) {
      addLog(`${card.name} — no other (Triple Ship Alliance) Unit in play. No effect.`); return;
    }
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length === 0) {
      addLog(`${card.name} — deck empty, no draw. No discard required.`); broadcastCounters(); render(); return;
    }
    state.hand.push(drawn[0]);
    addLog(`${card.name} — drew ${drawn[0].name}.`);
    broadcastCounters(); render();
    showCardPickerModal(card.name, 'Choose 1 card from your hand to discard.',
      [...state.hand], (target) => {
        if (target) {
          state.hand = state.hand.filter(c => c.uid !== target.uid);
          updateTempState(target.cardNo, target.copyVer, 'trash');
          state.trashCount++;
          state.playerTrash.push(toTrashCard(target));
          broadcastLog(`${card.name} — discarded ${target.name}.`);
          broadcastCardToast(`${target.name} was discarded due to ${card.name} effect.`);
        }
        broadcastCounters(); render();
      }, card, { mandatory: true });
  },
});

// GD03-073 "Graze Ein" — Unit
// <Blocker> native.
// [During Link][Activate･Action][Once per Turn] (no RES cost)
// Conditions: linked, 6+ (Gjallarhorn) in trash, and must be in a battle
// against an enemy Unit. Effect: battling enemy Unit gets AP-3 this battle.
registerCardEffect('GD03-073', {
  canAction: (card, ctx) => {
    if (!isLinkUnit(card)) return false;
    if (state.playerTrash.filter(c => (c.trait || '').includes('(Gjallarhorn)')).length < 6) return false;
    // ctx is the raw battle context (attacker/defender at top level).
    // During end phase the step has no battle, so ctx.attacker will be undefined.
    if (!ctx?.attacker) return false;
    const enemyUnit = ctx.attacker?.uid === card.uid ? ctx.defender
                    : ctx.defender?.uid === card.uid ? ctx.attacker : null;
    if (!enemyUnit || ctx.isShield || ctx.isDirect) return false;
    if (enemyUnit.type !== 'unit' && enemyUnit.type !== 'unit-token') return false;
    return true;
  },
  action: (card, ctx) => {
    // When called from resolveUnitActionActivate the battle context is nested
    // under ctx.battle; fall back to ctx itself for any direct call path.
    const b = ctx.battle || ctx;
    const enemyUnit = b.attacker?.uid === card.uid ? b.defender
                    : b.defender?.uid === card.uid ? b.attacker : null;
    if (!enemyUnit) {
      addLog(`${card.name} — no battling enemy Unit found.`);
      ctx.onDone(); return true;
    }
    applyApMod([enemyUnit], -3, 'battleApMod');
    addLog(`${card.name} — ${enemyUnit.name} gets AP-3 this battle.`);
    broadcastCounters(); render();
    ctx.onDone();
    return true;
  },
});

// GD03-074 "Tieren Taozi" — Unit
// [During Pair] Passive: while another (Superpower Bloc) Unit is in play and
// this Unit is rested, enemy Units MUST choose it as their attack target.
// forcedAttackTarget hook is read by getForcedAttackTargetUid('opp') on the
// attacker's client — returning the UID blocks all other attack targets.
// card.pilot and card.rested are synced to state.oppField via PLAY_CARD and
// UNIT_REST, so the check is accurate on the opponent's client.
registerCardEffect('GD03-074', {
  forcedAttackTarget: (card, ownerSide) => {
    if (!card.rested) return false;
    if (!card.pilot) return false;
    return state[`${ownerSide}Field`].some(c => c && c.uid !== card.uid && (c.trait || '').includes('Superpower Bloc'));
  },
});

// GD03-075 "Super Gundam" — Unit
// [During Link][Attack] Choose 1 enemy Unit with no paired Pilot. It gets AP-2 this turn.
registerCardEffect('GD03-075', {
  attack: (card, ctx) => {
    if (!isLinkUnit(card)) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c && !c.pilot);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units without a Pilot.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with no paired Pilot. It gets AP-2 this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 this turn.`);
        render();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// GD03-076 "Freedom Gundam (METEOR)" — Unit
// [Once per Turn] Passive: during the player's turn, when a (Triple Ship
// Alliance) Unit deals non-lethal battle damage to an enemy Unit, may return
// that enemy Unit to its owner's hand.
// Fires via onBattleDamageSurvived hook in runDamageStep; passiveUsedThisTurn
// enforces the once-per-turn limit (cleared by UNREST_ALL).
registerCardEffect('GD03-076', {
  onBattleDamageSurvived: (card, _attacker, defender, runEpilogue) => {
    showCardPickerModal(
      card.name,
      `${_attacker.name} damaged ${defender.name} (not destroyed). Return it to its owner's hand?`,
      [defender],
      (target) => {
        if (target) {
          bounceEnemyUnit(defender);
          addLog(`${card.name} — ${defender.name} returned to opponent's hand.`);
          render();
        }
        runEpilogue();
      },
      card,
      { mandatory: false }
    );
  },
});

// GD03-077 "Justice Gundam (METEOR)" — Unit
// [When Linked] Choose 1–3 enemy Units with 3 or less HP. Return them to hand.
registerCardEffect('GD03-077', {
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 3 or less HP.`);
      return;
    }
    showCardPickerModalMulti(
      card.name,
      'Choose 1–3 enemy Units with 3 or less HP to return to their owner\'s hand.',
      candidates,
      { min: 1, max: 3, mandatory: true },
      (targets) => {
        targets.forEach(t => {
          bounceEnemyUnit(t);
          addLog(`${card.name} — ${t.name} returned to hand.`);
        });
        if (targets.length) render();
      },
      card
    );
  },
});

// GD03-079 "G-Defenser" — Unit
// Passive: when the player's Base would be rested as a cost to activate its
// effect, the player may rest this Unit instead (leaving the Base active).
// The hook is read by showBaseRestSubstituteModal in game.js, which fires
// before dispatch('ACTIVATE_BASE') when cost.restBase is true and an active
// unit with this hook is on the field.
registerCardEffect('GD03-079', {
  baseRestSubstitute: true,
});

// GD03-080 "Gundam Kimaris Trooper (Trooper Mode)" — Unit
// [When Linked] Choose 1 (Gjallarhorn) Command card from trash. Add it to hand.
registerCardEffect('GD03-080', {
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    const candidates = state.playerTrash.filter(c =>
      (c.type === 'command' || c.type === 'command pilot') &&
      (c.trait || '').includes('(Gjallarhorn)')
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no (Gjallarhorn) Command cards in trash.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 (Gjallarhorn) Command card from your trash. Add it to your hand.', candidates, (target) => {
      if (!target) return;
      const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
      if (idx !== -1) {
        state.playerTrash.splice(idx, 1);
        state.trashCount = Math.max(0, state.trashCount - 1);
      }
      updateTempState(target.cardNo, target.copyVer, 'hand');
      state.hand.push(target);
      addLog(`${card.name} — ${target.name} recovered from trash to hand.`);
      broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
      broadcastCounters(); render();
    }, card, { mandatory: true });
  },
});

// GD03-078 "Tieren High Mobility Type" — Unit
// [During Link][Destroyed] Return the card paired with this Unit to your hand.
// The pilot is pre-queued in the runDamageStep trash array before this trigger
// fires. ctx.rescuePilot() splices it out so it never reaches state.playerTrash.
// card.pilot is still populated at unit-trigger time (toTrashCard makes a copy,
// not modifying the original object).
registerCardEffect('GD03-078', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    if (!isLinkUnit(card)) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (ownerSide !== 'player') return;
    if (!card.pilot) return;
    ctx.rescuePilot?.();
    const pilot = card.pilot;
    state.hand.push(pilot);
    updateTempState(pilot.cardNo, pilot.copyVer, 'hand');
    addLog(`${card.name} — ${pilot.name} returned to your hand.`);
    broadcastCounters();
    render();
  },
});

// GD02-069 "Zeta Gundam" — Unit
// [During Link][Activate･Main][Once per Turn] (free) — unit must be rested, linked,
// and player must have an active Base. Rest the Base; set this Unit active;
// unit cannot target enemy Base or Shields this turn (tempCantAttackNonUnit).
registerCardEffect('GD02-069', {
  canActivate: (card) => {
    if (!isLinkUnit(card)) return false;
    if (!card.rested) return false;
    if (!state.playerBase || state.playerBase.rested) return false;
    return true;
  },
  activate: (card, ctx) => {
    setRested(state.playerBase, true);
    addLog(`${card.name} — ${state.playerBase.name} rested.`);
    setRested(card, false);
    card.tempCantAttackNonUnit = true;
    addLog(`${card.name} — set active. Cannot target enemy Base or Shields this turn.`);
    broadcastCounters(); render();
    ctx.onDone();
  },
});

// GD02-070 "Gundam Kimaris" — Unit
// [Deploy] If 4 or more (Gjallarhorn) cards in trash, draw 2. If you do, discard 2.
registerCardEffect('GD02-070', {
  deploy: (card) => {
    const gjalCount = state.playerTrash.filter(c => (c.trait || '').includes('(Gjallarhorn)')).length;
    if (gjalCount < 4) {
      addLog(`${card.name} — fewer than 4 (Gjallarhorn) cards in trash (${gjalCount}). No effect.`);
      return;
    }
    const drawn = drawCardsFromTempDeck(2, true);
    if (drawn.length === 0) {
      addLog(`${card.name} — deck empty, no cards drawn. No discard required.`);
      broadcastCounters(); render(); return;
    }
    drawn.forEach(c => state.hand.push(c));
    addLog(`${card.name} — drew ${drawn.map(c => c.name).join(', ')}.`);
    broadcastCounters(); render();

    showCardPickerModalMulti(
      card.name,
      'Choose 2 cards from your hand to discard to the trash.',
      [...state.hand],
      { min: 2, max: 2, mandatory: true },
      (targets) => {
        targets.forEach(target => {
          state.hand = state.hand.filter(c => c.uid !== target.uid);
          updateTempState(target.cardNo, target.copyVer, 'trash');
          state.trashCount++;
          state.playerTrash.push(toTrashCard(target));
          broadcastLog(`${card.name} — discarded ${target.name}.`);
          broadcastCardToast(`${target.name} was discarded due to ${card.name} effect.`);
        });
        broadcastCounters(); render(); setupDropZones();
      },
      card
    );
  },
});

// GD02-071 "Gundam Mk-II (AEUG)" — Unit
// [Deploy] If a friendly white Base is in play, you may pair 1 (AEUG) Pilot
// card from your hand with this Unit (for free — no RES cost).
// Pairing is done inline rather than via PAIR_PILOT dispatch to avoid spending
// the pilot's cost. The updated field slot is sent to the peer with PLAY_CARD
// so the opponent sees the pilot, same as a normal pair.
registerCardEffect('GD02-071', {
  deploy: (card) => {
    if (!state.playerBase || (state.playerBase.color || '').toLowerCase() !== 'white') {
      addLog(`${card.name} — no friendly white Base in play. No effect.`);
      return;
    }
    const candidates = state.hand.filter(c =>
      (c.canPilot || c.type === 'pilot' || c.type === 'command pilot') &&
      (c.trait || '').includes('(AEUG)')
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no (AEUG) Pilot cards in hand.`);
      return;
    }
    showCardPickerModal(card.name, 'You may pair 1 (AEUG) Pilot from your hand with this Unit (free).', candidates, (target) => {
      if (!target) { addLog(`${card.name} — pairing skipped.`); return; }
      const slot = state.playerField.findIndex(c => c && c.uid === card.uid);
      if (slot === -1) return;
      state.hand = state.hand.filter(c => c.uid !== target.uid);
      const unit = state.playerField[slot];
      unit.pilot = target;
      if (isLinkUnit(unit)) {
        unit.linkedThisTurn = true;
        // Same fix as GD01-023 Char's Gelgoog (green.js): this is deployed
        // and paired in the same [Deploy] trigger, so it's still carrying
        // deploy sickness at this point — newly satisfying the Link
        // requirement removes that, same as the standard PAIR_PILOT action.
        unit.tempCantAttack = false;
        if (state.playerBase) {
          window.CardEffects?.[state.playerBase.cardNo]?.onPlayerUnitLinked?.(state.playerBase, unit, slot);
        }
      }
      addLog(`${card.name} — ${target.name} paired for free.`);
      triggerCardEffect(target, 'pair', { unit, slot });
      triggerCardEffect(unit, 'pair', { unit, slot });
      NET.send({ type: 'PLAY_CARD', slot, card: unit, counters: myCounters() });
      broadcastCounters();
      render();
      setupDropZones();
    }, card);
  },
});

// GD02-074 "Gundam Aerial Rebuild" — Unit
// <High-Maneuver> native. [During Pair] Passive: while 4+ Command (or Command
// Pilot) cards in trash, this Unit gains <Blocker>.
registerCardEffect('GD02-074', {
  passiveKeywords: (card, ownerSide) => {
    if (!card.pilot) return [];
    const trash = ownerSide === 'player' ? state.playerTrash : state.oppTrash;
    const count = trash.filter(c => c.type === 'command' || c.type === 'command pilot').length;
    return count >= 4 ? ['Blocker'] : [];
  },
});

// GD02-075 "Rick Dias (Red)" — Unit
// [Attack] Choose 1 active friendly Base. Rest it. If you do, choose 1 enemy
// Unit Lv.4 or lower. It gets AP-2 during this battle.
// If no active base, the effect is skipped. If the base is rested but no
// valid targets exist, the base remains rested (no "may" on the resting).
registerCardEffect('GD02-075', {
  attack: (card, ctx) => {
    if (!state.playerBase || state.playerBase.rested) { ctx.onDone(); return; }
    const baseName = state.playerBase.name;
    setRested(state.playerBase, true);
    addLog(`${card.name} — ${baseName} rested.`);
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.4 or lower.`);
      render();
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit Lv.4 or lower. It gets AP-2 this battle.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 this battle.`);
        render();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// GD02-076 "Buster Gundam" — Unit
// Passive: while this Unit has 5 or more AP, it gains <Blocker>.
// effectiveBp(card) is used so pilot bonuses, tempApMod, and passiveUnitApBonus
// are all included — the condition re-evaluates live on every hasNativeKeyword
// check so the badge appears/disappears as AP changes.
registerCardEffect('GD02-076', {
  passiveKeywords: (card) => effectiveBp(card) >= 5 ? ['Blocker'] : [],
});

// GD02-072 "Hyaku-Shiki" — Unit
// <Blocker> native. Passive: gains <Repair 1> while a friendly white Base is in play.
// conditionalRepair hook overrides effect-text parsing in getRepairValue so that
// the "Repair 1" in card.effect doesn't apply unconditionally.
registerCardEffect('GD02-072', {
  conditionalRepair: () =>
    state.playerBase && (state.playerBase.color || '').toLowerCase() === 'white' ? 1 : 0,
});

// GD02-073 "Carta's Graze Ritter (Ground Type)" — Unit
// Passive (opponent's turn): the enemy Unit battling this Unit gains First Strike.
// "Opponent's turn" is implicit — this Unit is only the DEFENDER when the opponent
// attacks, so the hook fires exactly when the condition is satisfied on both clients.
registerCardEffect('GD02-073', {
  grantsFirstStrikeToAttacker: () => true,
});

// GD02-081 "Methuss" — Unit
// [Deploy] If a friendly white Base is in play, choose 1 enemy Unit.
// It gets AP-2 during this turn.
registerCardEffect('GD02-081', {
  deploy: (card) => {
    if (!state.playerBase || (state.playerBase.color || '') !== 'white') {
      addLog(`${card.name} — no friendly white Base in play. No effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units to target.`);
      return;
    }
    pickAndApplyApMod(card, candidates, -2, 'tempApMod', 'Choose 1 enemy Unit. It gets AP-2 during this turn.', () => {});
  },
});

// GD02-082 "Gaelio's Schwalbe Graze" — Unit
// Passive: while another (Gjallarhorn) Unit is in play (excluding itself),
// this Unit gains <Blocker>.
registerCardEffect('GD02-082', {
  passiveKeywords: (card, ownerSide) =>
    state[`${ownerSide}Field`].some(c => c && c.uid !== card.uid && (c.trait || '').includes('Gjallarhorn'))
      ? ['Blocker'] : [],
});

// GD02-083 "Graze Ritter (Ground Type)" — Unit
// [Destroyed] If it is your opponent's turn, choose 1 of your rested
// (Gjallarhorn) Units and set it as active.
// The destroyed card is already removed from playerField before this fires,
// so it won't appear in the candidate list.
registerCardEffect('GD02-083', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (ownerSide !== 'player') return;
    if (state.myTurn) return;
    const candidates = state.playerField.filter(c => c && c.rested && (c.trait || '').includes('Gjallarhorn'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested (Gjallarhorn) Units to set active.`); return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your rested (Gjallarhorn) Units. Set it as active.',
      candidates, (target) => {
        if (!target) return;
        setRested(target, false);
        addLog(`${card.name} — ${target.name} set to active.`);
        render();
      }, card);
  },
});

// GD02-129 "Argama"
// This Base can't receive enemy effect damage. (Static while in play — set
// once on deploy via the permanent card.cantReceiveEffectDamage flag; see
// dealDamageToBase()/registry.js.)
registerCardEffect('GD02-129', {
  deploy: (card) => {
    card.cantReceiveEffectDamage = true;
    addLog(`${card.name} can't receive enemy effect damage.`);
  },
});

// GD03-131 "Eternal"
// [Deploy] ...Then, if you have 2 or more (Triple Ship Alliance) Units in
// play, choose 1 enemy Unit that is Lv.4 or lower. Return it to its owner's
// hand.
registerCardEffect('GD03-131', {
  deploy: (card) => {
    const count = state.playerField.filter(c => c && (c.trait || '').includes('Triple Ship Alliance')).length;
    if (count < 2) {
      addLog(`${card.name} — fewer than 2 friendly (Triple Ship Alliance) Units in play. No effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
    pickAndBounceOne(card, candidates, "Choose 1 enemy Unit (Lv.4 or lower). Return it to its owner's hand.", () => {});
  },
});

// GD03-132 "Radish"
// [Destroyed] If you have an (AEUG) Link Unit in play, choose 1 enemy Unit
// with 4 or less HP. Rest it.
// (runDamageStep's three 'destroyed' call sites fire on BOTH clients for the
// same shared ATTACK — ctx.defenderSide is 'player' only on the client that
// actually controls the destroyed Base, since "you" in the card text means
// Radish's own controller. Guard on it so the picker only ever opens on that
// client; the other client just no-ops here.)
registerCardEffect('GD03-132', {
  destroyed: (card, ctx) => {
    if (!ctx || ctx.defenderSide !== 'player') return;
    const hasAeugLink = state.playerField.some(c => c && isLinkUnit(c) && (c.trait || '').includes('AEUG'));
    if (!hasAeugLink) {
      addLog(`${card.name} — no friendly (AEUG) Link Unit in play. No effect.`);
      ctx.onDone?.();
      return;
    }
    const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Unit with 4 or less HP. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(card.name, "Choose 1 enemy Unit with 4 or less HP. Rest it.", candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render();
        setupDropZones();
      }
      ctx.onDone?.();
    }, card);
  },
});

// GD03-081 "AEU Enact Demonstration Color" — Unit
// Passive: can only attack during a turn when the player deployed a
// (Superpower Bloc) or (UN) Unit. Greyscale (cant-attack) otherwise.
// state.superpowerDeployedThisTurn is set by PLAY_CARD in game.js and reset
// by CLEAR_TURN_EFFECTS / UNREST_ALL each turn.
registerCardEffect('GD03-081', {
  cantAttack: () => !state.superpowerDeployedThisTurn,
});

// GD03-082 "Union Flag" — Unit
// Passive (in hand): while 2 or more (Superpower Bloc) or (UN) Units are on
// the player's field, this card's play cost is reduced by 1 (2 → 1).
// handCostReduction is read by effectiveHandCost() in game.js, which feeds
// canPlayCard (hand dimming), handleDrop cost gate, and PLAY_CARD spendResources.
registerCardEffect('GD03-082', {
  handCostReduction: () => {
    const count = state.playerField.filter(c => c && ((c.trait || '').includes('Superpower Bloc') || (c.trait || '').includes('(UN)'))).length;
    return count >= 2 ? 1 : 0;
  },
});

// ST01-006 "Gundam Aerial (Permet Score Six)" — Unit
// [When Paired] Choose 1 enemy Unit Lv.5 or lower. It gets AP-3 this turn.
registerCardEffect('ST01-006', {
  pair: (card) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.5 or lower.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit Lv.5 or lower. It gets AP-3 this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -3, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-3 this turn.`);
        render();
      }
    }, card, { mandatory: true });
  },
});

// ST01-009 "Zowort" — Unit
// Passive: this Unit cannot choose the enemy player (base / shield / direct)
// as its attack target — only enemy Units are valid attack targets.
registerCardEffect('ST01-009', {
  cantAttackNonUnit: () => true,
});

// ST01-011 "Suletta Mercury" — Pilot
// [Attack][Once per Turn] Choose 1 of your Resources. Set it as active.
// Fires on both paired and linked attacks (no [During Link]). Recovers 1
// spent RES: if the current active RES (state.res) is already at the turn's
// base maximum (state.resBase), the effect does nothing.
registerCardEffect('ST01-011', {
  attack: (card, ctx) => {
    if (card.passiveUsedThisTurn) { ctx.onDone?.(); return; }
    if (state.res >= state.resBase) {
      addLog(`${card.name} — all RES already active, no effect.`);
      ctx.onDone?.();
      return;
    }
    card.passiveUsedThisTurn = true;
    state.res = Math.min(state.res + 1, state.resBase);
    state.resourceSetActiveThisTurn = true;
    addLog(`${card.name} — recovered 1 RES (now ${state.res}/${state.resBase}).`);
    broadcastCounters();
    render();
    ctx.onDone?.();
  },
});

// ST09-004 "Freedom Gundam" — Unit
// <Blocker> native. Passive: while a friendly Base (any color) is in play,
// this Unit gains <Suppression>. ownerSide picks the correct base reference.
registerCardEffect('ST09-004', {
  passiveKeywords: (card, ownerSide) => {
    const base = ownerSide === 'player' ? state.playerBase : state.oppBase;
    return base ? ['Suppression'] : [];
  },
});

// ST05-007 "McGillis' Schwalbe Graze" — Unit
// [When Paired] Choose 1 enemy Unit Lv.3 or lower. It gets AP-2 this turn.
// Fires via unit's 'pair' trigger (added to PAIR_PILOT alongside the pilot's).
// Mandatory pick when targets exist; silent skip when none.
registerCardEffect('ST05-007', {
  pair: (card) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.3 or lower.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit Lv.3 or lower. It gets AP-2 this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 this turn.`);
        render();
      }
    }, card, { mandatory: true });
  },
});

// ST05-012 "McGillis Fareed" — Pilot
// [When Paired] If you have 2 or more other (Gjallarhorn)/(Tekkadan) Units in
// play, choose 1 enemy Unit with 3 or less HP. Rest it.
// "Other" explicitly excludes the unit McGillis was just paired with.
registerCardEffect('ST05-012', {
  pair: (card, ctx) => {
    const otherCount = state.playerField.filter(c =>
      c && c.uid !== ctx.unit.uid &&
      ((c.trait || '').includes('(Gjallarhorn)') || (c.trait || '').includes('(Tekkadan)'))
    ).length;

    if (otherCount < 2) {
      addLog(`${card.name} — fewer than 2 other (Gjallarhorn)/(Tekkadan) Units in play (${otherCount}). No effect.`);
      return;
    }

    const candidates = state.oppField.filter(c =>
      c && !c.rested && !c.isShield && !c.isDirect && effectiveHp(c) <= 3
    );

    if (candidates.length === 0) {
      addLog(`${card.name} — no active enemy Units with 3 or less HP. No effect.`);
      return;
    }

    showCardPickerModal(
      card.name,
      'Choose 1 active enemy Unit with 3 or less HP. Rest it.',
      candidates,
      (target) => {
        if (target) {
          setRested(target, true);
          addLog(`${card.name} — rested ${target.name}.`);
          render();
          setupDropZones();
        }
      },
      card
    );
  },
});

// ST04-010 "Kira Yamato" — Pilot
// [Attack] Choose 1 enemy Unit. It gets AP-2 during this battle.
// No [During Link] qualifier — fires when paired OR linked.
// Fires regardless of attack target (unit, base, shield, or direct) — the
// chosen unit is debuffed even when the attacker is hitting a base/shield.
// Uses battleApMod so the debuff is captured by runDamageStep's stat read and
// then cleared (along with all other field units' battleApMod) when the damage
// step resolves — it does NOT persist beyond this single attack.
registerCardEffect('ST04-010', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }

    const candidates = state.oppField.filter(c => c && !c.isShield && !c.isDirect);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units in play. No effect.`);
      ctx.onDone?.();
      return;
    }

    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. It gets AP-2 during this battle.',
      candidates,
      (target) => {
        if (target) {
          applyApMod([target], -2, 'battleApMod');
          addLog(`${card.name} — ${target.name} gets AP-2 during this battle.`);
          render();
          setupDropZones();
        }
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── EB01-069 · Beside Pain — Pilot ───────────────────────────────────────
// [Burst] Add this card to your hand — handled generically (literal
// "[Burst]Add this card to your hand." pattern, see registry.js).
// [Attack] Choose 1 friendly Unit with <Blocker>. It gets AP+2 during this
// turn.
// No [During Link] qualifier — fires when paired OR linked, same convention
// as ST04-010 Kira Yamato. Uses tempApMod ("during this turn"), not
// battleApMod, since the card text says "during this turn".
registerCardEffect('EB01-069', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }

    const candidates = state.playerField.filter(c => c && hasNativeKeyword(c, 'Blocker'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no friendly Units with <Blocker> in play. No effect.`);
      ctx.onDone?.();
      return;
    }

    showCardPickerModal(
      card.name,
      'Choose 1 friendly Unit with <Blocker>. It gets AP+2 during this turn.',
      candidates,
      (target) => {
        if (target) {
          applyApMod([target], 2, 'tempApMod');
          addLog(`${card.name} — ${target.name} gets AP+2 during this turn.`);
          render();
          setupDropZones();
        }
        ctx.onDone?.();
      },
      card,
      { mandatory: true }
    );
  },
});

// GD04-070 "Al-Saachez's AEU Enact Custom Moralia Development Experiment Type" — Unit
// [Deploy] You may pair 1 Pilot card with "Ali al-Saachez" in its card name
// from your hand with this Unit — no resource cost paid.
// Inline the pair logic (instead of dispatch('PAIR_PILOT', ...)) to skip
// spendResources; mirror the updated slot via NET.send PLAY_CARD afterward.
registerCardEffect('GD04-070', {
  deploy: (card, ctx) => {
    const candidates = state.hand.filter(c => c.type === 'pilot' && (c.name || '').includes('Ali al-Saachez'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no "Ali al-Saachez" Pilot in hand.`);
      return;
    }
    const slot = ctx.slot ?? state.playerField.findIndex(c => c && c.uid === card.uid);
    showCardPickerModal(
      card.name,
      'You may pair 1 "Ali al-Saachez" Pilot from your hand with this Unit (free cost).',
      candidates,
      (pilot) => {
        if (!pilot) return;
        const pairedUnit = { ...state.playerField[slot], pilot };
        if (isLinkUnit(pairedUnit)) {
          pairedUnit.tempCantAttack = false;
          // Was missing linkedThisTurn (needed for a paired pilot's own
          // endOfTurnLinked hooks to fire) and the base notification (e.g.
          // ST06-015 Kaneban) — same standard PAIR_PILOT side effects every
          // other inline-pairing card effect applies.
          pairedUnit.linkedThisTurn = true;
          if (state.playerBase) {
            window.CardEffects?.[state.playerBase.cardNo]?.onPlayerUnitLinked?.(state.playerBase, pairedUnit, slot);
          }
        }
        state.playerField[slot] = pairedUnit;
        state.hand = state.hand.filter(c => c.uid !== pilot.uid);
        updateTempState(pilot.cardNo, pilot.copyVer, 'field');
        addLog(`${card.name} — paired ${pilot.name} for free.`);
        NET.send({ type: 'PLAY_CARD', slot, card: pairedUnit, counters: myCounters() });
        broadcastCounters();
        triggerCardEffect(pilot, 'pair', { unit: pairedUnit, slot });
        render();
        setupDropZones();
      },
      card
    );
  },
});

// GD04-065 "Unicorn Gundam 02 Banshee Norn (Destroy Mode)" — Unit
// Effect 1: [During Link][Activate･Main] (no RES cost) while rested + 3+ blue
//   cards (any type) in trash. Exile 3 blue cards from trash → set active +
//   tempCantAttackNonUnit.
// Effect 2: [Attack] All enemy Units get AP-1 this turn (no pilot required).

registerCardEffect('GD04-065', {
  canActivate: (card) => {
    if (!isLinkUnit(card)) return false;
    if (!card.rested) return false;
    return state.playerTrash.filter(c =>
      (c.color || '').toLowerCase() === 'blue'
    ).length >= 3;
  },
  activate: (card, ctx) => {
    const candidates = state.playerTrash.filter(c =>
      (c.color || '').toLowerCase() === 'blue'
    );
    showCardPickerModalMulti(
      card.name,
      'Choose exactly 3 blue cards from your trash to exile.',
      candidates,
      { min: 3, max: 3, mandatory: true },
      (targets) => {
        if (!targets.length) { ctx.onDone(); return; }
        targets.forEach(target => {
          if (state.playerTrash.some(c => c.uid === target.uid)) {
            exileFromTrash(target);
            addLog(`${card.name} — exiled ${target.name}.`);
          }
        });
        setRested(card, false);
        card.tempCantAttackNonUnit = true;
        addLog(`${card.name} — set active. Cannot target enemy Base or Shields this turn.`);
        broadcastCounters(); render();
        ctx.onDone();
      },
      card
    );
  },
  attack: (card, ctx) => {
    const enemies = state.oppField.filter(c => c);
    if (enemies.length > 0) {
      applyApMod(enemies, -1, 'tempApMod');
      addLog(`${card.name} — all enemy Units get AP-1 this turn.`);
      render();
    }
    ctx.onDone();
  },
});

// GD04-066 "Unicorn Gundam (Awakened)" — Unit
// <Suppression> native.
// Passive: when the player activates a Command's [Main]/[Action] effect,
// choose 1 enemy Unit → AP-2 this turn.
// Fires via fireCommandEffectPassives → onCommandEffect(unit, cmdCard, next).
registerCardEffect('GD04-066', {
  onCommandEffect: (card, _cmdCard, next) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { next(); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-2 this turn. (optional)', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 this turn.`);
        render();
      }
      next();
    }, card, { mandatory: false });
  },
});

// Helper shared by GD04-067: extracts keywords that ∀ Gundam can copy.
// Keywords with values (Repair N, Breach N) are returned as "Repair 2" etc.
// so getRepairValue/getBreachValue regex picks them up from tempKeywords.
const GD04067_QUALIFYING = ['Repair', 'Breach', 'First Strike', 'Support', 'High-Maneuver', 'Suppression', 'Blocker'];
function gd04067ExtractKeywords(card) {
  const raw = card.nativeKeyword || '';
  return (raw.match(/\[([^\]]+)\]/g) || [])
    .map(m => m.slice(1, -1).trim())
    .filter(kw => GD04067_QUALIFYING.some(q => kw === q || kw.startsWith(q + ' ')));
}

// GD04-067 "∀ Gundam" (alt) — Unit
// [Activate･Main][Once per Turn]①: Choose 1 Unit card with a qualifying
// keyword from your trash. This Unit gains AP+1 and all those keywords
// during this turn.
registerCardEffect('GD04-067', {
  canActivate: (card) => {
    if ((state.res + state.exRes) < 1) return false;
    return state.playerTrash.some(c => c.type === 'unit' && gd04067ExtractKeywords(c).length > 0);
  },
  activate: (card, ctx) => {
    const candidates = state.playerTrash.filter(c => c.type === 'unit' && gd04067ExtractKeywords(c).length > 0);
    if (candidates.length === 0) {
      addLog(`${card.name} — no eligible Units in trash.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 Unit with a qualifying keyword from your trash. This Unit gains AP+1 and its keywords this turn.', candidates, (target) => {
      if (!target) {
        // Player cancelled — restore resources and reset the once-per-turn gate.
        if (ctx.resSnapshot) { state.res = ctx.resSnapshot.res; state.exRes = ctx.resSnapshot.exRes; }
        card.activateMainUsedThisTurn = false;
        broadcastCounters(); render(); setupDropZones();
        ctx.onDone(); return;
      }
      // Resources were already spent by showUnitActivateModal — do NOT call spendResources again.
      const keywords = gd04067ExtractKeywords(target);
      card.tempKeywords = [...(card.tempKeywords || []), ...keywords];
      applyApMod([card], 1, 'tempApMod');
      addLog(`${card.name} — gains AP+1 and [${keywords.join('], [')}] this turn (from ${target.name}).`);
      broadcastCounters();
      render();
      ctx.onDone();
    }, card, { skipLabel: 'Cancel' });
  },
});

// GD04-068 "Silver Bullet" — Unit
// <Blocker> native. Passive: when this Unit receives effect damage from an enemy,
// reduce that damage by 3 (minimum 0). Applies to all sources of effect damage:
// command cards, unit effects, base effects, command pilot effects, etc.
registerCardEffect('GD04-068', {
  effectDamageReduction: () => 3,
});

// GD04-069 "∀ Gundam" (alt) — Unit
// <Blocker> — native keyword.
// [During Link] End of turn: if the player paid ① or more for a
// (Militia)/(Dianna Counter) unit's effect this turn, choose 1 rested
// (Militia) unit and set it active.
// state.militiaOrDiannaEffectPaidThisTurn is set in showUnitActivateModal
// when a qualifying unit's effect is paid; cleared by CLEAR_TURN_EFFECTS.
registerCardEffect('GD04-069', {
  endOfTurn: (card, onDone) => {
    if (!isLinkUnit(card) || !state.militiaOrDiannaEffectPaidThisTurn) { onDone(); return; }
    const candidates = state.playerField.filter(c => c && c.rested && (c.trait || '').includes('(Militia)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested (Militia) Units to set active.`);
      onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 of your rested (Militia) Units. Set it as active.', candidates, (target) => {
      if (target) {
        setRested(target, false);
        addLog(`${card.name} — ${target.name} set to active.`);
        render();
      }
      onDone();
    }, card, { mandatory: true });
  },
});

// GD04-071 "Graham's Union Flag Custom II (GN Flag)" — Unit
// [Burst] If an enemy (CB) Unit is in play, add this card to your hand.
//   The card falls to trash first (hasBurst regex doesn't match the
//   conditional text), then the burst handler rescues it if the condition holds.
// [Activate･Main] (no RES cost — cost is exiling 2 trash cards):
//   Unit must be RESTED to activate. Choose 1 (Superpower Bloc) card and 1
//   (UN) card from trash (ANY card type — the printed text says "card", not
//   "Unit"), exile them; set this Unit active, tempCantAttack = true.
registerCardEffect('GD04-071', {
  burst: (card) => {
    const hasCB = state.oppField.some(c => c && (c.trait || '').includes('(CB)'));
    if (!hasCB) {
      addLog(`${card.name} — no enemy (CB) Units. Burst condition not met.`);
      return;
    }
    // Card was just added to playerTrash by the normal non-burst path; remove it.
    const idx = state.playerTrash.findIndex(c => c.uid === card.uid);
    if (idx !== -1) {
      state.playerTrash.splice(idx, 1);
      state.trashCount = Math.max(0, state.trashCount - 1);
    }
    updateTempState(card.cardNo, card.copyVer, 'hand');
    state.hand.push(card);
    addLog(`${card.name} — [Burst] added to hand (enemy (CB) Unit in play).`);
    glowHandCard(card.uid);
    broadcastCounters(); render();
  },
  canActivate: (card) => {
    if (!card.rested) return false;
    const hasSP = state.playerTrash.some(c => (c.trait || '').includes('(Superpower Bloc)'));
    const hasUN = state.playerTrash.some(c => (c.trait || '').includes('(UN)'));
    return hasSP && hasUN;
  },
  activate: (card, ctx) => {
    const spCandidates = state.playerTrash.filter(c => (c.trait || '').includes('(Superpower Bloc)'));
    const unCandidates = state.playerTrash.filter(c => (c.trait || '').includes('(UN)'));
    if (spCandidates.length === 0 || unCandidates.length === 0) {
      addLog(`${card.name} — not enough qualifying cards in trash.`); ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 (Superpower Bloc) card from your trash to exile.', spCandidates, (sp) => {
      if (!sp) { ctx.onDone(); return; }
      const remainingUN = unCandidates.filter(c => c.uid !== sp.uid);
      if (remainingUN.length === 0) {
        addLog(`${card.name} — no (UN) card remains after selection.`); ctx.onDone(); return;
      }
      showCardPickerModal(card.name, 'Choose 1 (UN) card from your trash to exile.', remainingUN, (un) => {
        if (!un) { ctx.onDone(); return; }
        [sp, un].forEach(exiled => {
          if (state.playerTrash.some(c => c.uid === exiled.uid)) exileFromTrash(exiled);
        });
        setRested(card, false);
        card.tempCantAttack = true;
        addLog(`${card.name} — exiled ${sp.name} and ${un.name}. Set active (cannot attack this turn).`);
        broadcastCounters(); render();
        ctx.onDone();
      }, card, { mandatory: true });
    }, card, { mandatory: true });
  },
});

// GD04-072 "Unicorn Gundam 02 Banshee Norn (Unicorn Mode)" — Unit
// [When Linked] Choose 1 enemy Unit with 3 or less HP. Return it to owner's hand.
// Uses pair trigger + isLinkUnit check — link is set by PAIR_PILOT before trigger fires.
registerCardEffect('GD04-072', {
  pair: (card) => {
    if (!isLinkUnit(card)) return;
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 3 or less HP.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 3 or less HP. Return it to its owner\'s hand.', candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(`${card.name} — ${target.name} returned to opponent's hand.`);
        render();
      }
    }, card, { mandatory: true });
  },
});

// GD04-073 "∀ Gundam" — Unit
// [Activate･Main][Once per Turn]①: This Unit gets AP+2 during this turn.
// Cost (1 RES) and once-per-turn gate handled by showUnitActivateModal /
// canActivateUnitEffect. No canActivate hook needed — RES check is already
// built into canActivateUnitEffect. ctx.onDone signals the activate flow done.
registerCardEffect('GD04-073', {
  activate: (card, ctx) => {
    applyApMod([card], 2, 'tempApMod');
    addLog(`${card.name} — AP+2 this turn.`);
    render();
    ctx.onDone();
  },
});

// GD04-075 "GN-X" — Unit
// Passive (hand): cost reduced by the number of (UN)/(Superpower Bloc) command
// cards in the player's trash. effectiveHandCost() clamps total to 0 minimum.
registerCardEffect('GD04-075', {
  handCostReduction: () =>
    state.playerTrash.filter(c =>
      (c.type === 'command' || c.type === 'command pilot') &&
      ((c.trait || '').includes('(UN)') || (c.trait || '').includes('Superpower Bloc'))
    ).length,
});

// GD04-080 "Alvatore" — Unit
// [Destroyed] If you have another (UN)/(Superpower Bloc) Unit in play,
// deploy 1 rested Alvaaron (T-024) Unit token.
// Alvatore's field slot is already null'd before this trigger fires, so the
// freed slot is available for the token. setRested(true) after deployment
// mirrors the rested state to the peer via NET.send UNIT_REST.
registerCardEffect('GD04-080', {
  destroyed: (card, ctx) => {
    if (!ctx) return;
    const ownerSide = card.uid === ctx.defender?.uid ? ctx.defenderSide : ctx.attackerSide;
    if (ownerSide !== 'player') return;
    const hasQualifier = state.playerField.some(c => c && (
      (c.trait || '').includes('(UN)') || (c.trait || '').includes('Superpower Bloc')
    ));
    if (!hasQualifier) {
      addLog(`${card.name} — no (UN)/(Superpower Bloc) Unit in play. No token deployed.`); return;
    }
    const token = deployTokenToField('T-024');
    if (!token) {
      addLog(`${card.name} — field full, cannot deploy Alvaaron token.`); return;
    }
    setRested(token, true);
    addLog(`${card.name} — Alvaaron token deployed (rested).`);
    render();
  },
});

// GD04-074 "Kapool" — Unit
// [Attack] You may pay ①. If you do, draw 1. Then, discard 1.
// Fires before the block step via fireAttackerUnitAttackTrigger.
// ctx.onDone MUST be called on every path to advance the battle pipeline.
registerCardEffect('GD04-074', {
  attack: (card, ctx) => {
    if (state.res < 1) { ctx.onDone(); return; }

    const overlay = document.createElement('div');
    overlay.className = 'mk-overlay';
    const modal = document.createElement('div');
    modal.className = 'mk-modal';

    const title = document.createElement('div');
    title.className = 'mk-title';
    title.textContent = card.name;
    modal.appendChild(title);

    const body = document.createElement('div');
    body.className = 'mk-body';
    body.style.marginBottom = '14px';
    body.textContent = 'Pay ① to draw 1 card, then discard 1?';
    modal.appendChild(body);

    const btnRow = document.createElement('div');
    btnRow.className = 'base-activate-btns';

    const btnYes = document.createElement('button');
    btnYes.className = 'pair-btn confirm';
    btnYes.textContent = 'Pay ①';
    btnYes.addEventListener('click', () => {
      overlay.remove();
      spendResources(1);
      broadcastCounters();
      render();
      const drawn = drawCardsFromTempDeck(1, true);
      if (drawn.length === 0) {
        addLog(`${card.name} — deck empty, no draw. No discard required.`);
        broadcastCounters(); render(); ctx.onDone(); return;
      }
      state.hand.push(drawn[0]);
      addLog(`${card.name} — drew ${drawn[0].name}.`);
      broadcastCounters(); render();
      showCardPickerModal(card.name, 'Choose 1 card from your hand to discard.',
        [...state.hand], (target) => {
          if (target) {
            state.hand = state.hand.filter(c => c.uid !== target.uid);
            updateTempState(target.cardNo, target.copyVer, 'trash');
            state.trashCount++;
            state.playerTrash.push(toTrashCard(target));
            broadcastLog(`${card.name} — discarded ${target.name}.`);
            broadcastCardToast(`${target.name} was discarded due to ${card.name} effect.`);
          }
          broadcastCounters(); render(); ctx.onDone();
        }, card, { mandatory: true });
    });

    const btnNo = document.createElement('button');
    btnNo.className = 'pair-btn cancel';
    btnNo.textContent = 'Skip';
    btnNo.addEventListener('click', () => { overlay.remove(); ctx.onDone(); });

    btnRow.appendChild(btnYes);
    btnRow.appendChild(btnNo);
    modal.appendChild(btnRow);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
  },
});

// GD04-097 "Loran Cehack" — Pilot
// [When Linked] Choose 1 enemy Unit with 3 or less HP. Return it to its
// owner's hand.
// Uses the 'pair' trigger with an isLinkUnit guard — the effect only fires
// when the pilot actually meets the linked unit's link requirement.
registerCardEffect('GD04-097', {
  pair: (card, ctx) => {
    if (!isLinkUnit(ctx.unit)) return; // [When Linked] — skip if just paired, not linked
    const candidates = state.oppField.filter(c =>
      c && !c.isShield && !c.isDirect && effectiveHp(c) <= 3
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 3 or less HP. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with 3 or less HP. Return it to its owner\'s hand.',
      candidates,
      (target) => {
        if (target) {
          bounceEnemyUnit(target);
          addLog(isUnitToken(target)
            ? `${card.name} — ${target.name} (token) removed from the game.`
            : `${card.name} — returned ${target.name} to its owner's hand.`);
          render();
          setupDropZones();
        }
      },
      card
    );
  },
});

// GD04-098 "Riddhe Marcenas" — Pilot
// [During Link] When this Unit receives effect damage from an enemy, reduce it
// by 2. A 2-or-less hit is fully blocked (reduced to 0); a 3+ hit is reduced
// by 2 (e.g. 3 → 1). Does NOT affect normal combat damage — only effect damage
// routed through dealDamageToUnit(..., { fromEnemy: true }).
//
// Implemented via the `effectDamageReduction` hook, which dealDamageToUnit()
// consults dynamically (live lookup of target.pilot at damage time). No pair
// trigger is needed — the reduction is always active while the pilot is linked.
registerCardEffect('GD04-098', {
  effectDamageReduction: () => 2,
});

// GD04-099 "Ali al-Saachez" — Pilot
// [During Link][Attack] You may choose 1 enemy Pilot. Return it to its owner's
// hand. Fires regardless of attack target (unit, base, shield, or direct) —
// the chosen pilot is returned even when attacking a base/shield.
// Removing the pilot strips its AP/HP bonus from that unit — if the unit's raw
// HP (post-damage, without the pilot bonus) hits ≤ 0 it is immediately
// destroyed before the Damage Step.
//
// [During Link] enforced in-handler (isLinkUnit guard). Handler sends
// PILOT_UNPAIR first (so opponent adds pilot to hand), then OPP_UNIT_DESTROYED
// if the stripped unit dies. runDamageStep detects a gone defender via its
// defender-still-on-field guard and exits cleanly.
registerCardEffect('GD04-099', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(ctx.attacker)) { ctx.onDone?.(); return; } // [During Link]

    const candidates = state.oppField.filter(c => c && c.pilot);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Pilots on the field. No effect.`);
      ctx.onDone?.();
      return;
    }

    showCardPickerModal(
      card.name,
      "You may choose 1 enemy Pilot. Return it to its owner's hand.",
      candidates.map(u => u.pilot),
      (chosenPilot) => {
        if (!chosenPilot) {
          addLog(`${card.name} — skipped.`);
          ctx.onDone?.();
          return;
        }

        const slot = state.oppField.findIndex(c => c && c.pilot?.uid === chosenPilot.uid);
        const unit = state.oppField[slot];
        if (!unit) { ctx.onDone?.(); return; }

        // Strip pilot from unit (also clear any keywords the pilot passively granted)
        unit.pilot = null;
        unit.pilotKeywords = [];

        // PILOT_UNPAIR first — opponent moves pilot to their hand before unit
        // might be removed by OPP_UNIT_DESTROYED.
        NET.send({ type: 'PILOT_UNPAIR', unitUid: unit.uid, pilotUid: chosenPilot.uid });

        // Check if unit survives without pilot's HP bonus
        const unitRawHp = parseInt(unit.hp) || 0;
        if (unitRawHp <= 0) {
          addLog(`${card.name} — returned ${chosenPilot.name} to hand. ${unit.name} lost its HP boost and is destroyed!`);
          state.oppField[slot] = null;
          state.oppTrash.push(toTrashCard(unit));
          NET.send({ type: 'OPP_UNIT_DESTROYED', unitUid: unit.uid });
        } else {
          addLog(`${card.name} — returned ${chosenPilot.name} to opponent's hand.`);
        }

        render();
        setupDropZones();
        ctx.onDone?.();
      },
      card
    );
  },
});

// GD02-097 "Kamille Bidan" — Pilot
// While there is a friendly white Base in play, this Unit gets AP+2.
// Passive — no trigger. Checked live inside effectiveBp() via the
// passiveApBonus hook, so the bonus appears/disappears immediately when
// the base enters or leaves play without needing any state on the unit.
registerCardEffect('GD02-097', {
  passiveApBonus: (pilot, unit, base) => (base && base.color === 'white') ? 2 : 0,
});

// GD02-098 "Quattro Bajeena" — Pilot
// This card's name is also treated as [Char Aznable].
//   → pilotAliases: ['Char Aznable'] is auto-populated by buildGameCardFromDb /
//     enrichCardsFromDb from the "treated as [X]" pattern in effectData. Units
//     whose link requirement reads "Char Aznable" will pass isLinkUnit() when
//     this pilot is paired.
//
// [When Linked] If this is an (AEUG) Unit, draw 1. If you do, discard 1.
//   → pair trigger + isLinkUnit guard. The linked unit (ctx.unit) must have
//     (AEUG) in its trait. Discard is mandatory when the draw succeeds.
registerCardEffect('GD02-098', {
  pair: (card, ctx) => {
    if (!isLinkUnit(ctx.unit)) return; // [When Linked]
    if (!(ctx.unit.trait || '').includes('(AEUG)')) {
      addLog(`${card.name} — linked Unit does not have the (AEUG) trait. No effect.`);
      return;
    }

    // Draw 1
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length === 0) {
      addLog(`${card.name} — deck is empty, no draw. No discard required.`);
      broadcastCounters();
      render();
      return;
    }
    state.hand.push(drawn[0]);
    addLog(`${card.name} — drew ${drawn[0].name}.`);
    broadcastCounters();
    render();

    // Discard 1 (mandatory — "if you do, discard 1")
    showCardPickerModal(
      card.name,
      'Choose 1 card from your hand to discard (mandatory).',
      [...state.hand],
      (target) => {
        if (target) {
          state.hand = state.hand.filter(c => c.uid !== target.uid);
          updateTempState(target.cardNo, target.copyVer, 'trash');
          state.trashCount++;
          state.playerTrash.push(toTrashCard(target));
          broadcastLog(`${card.name} — discarded ${target.name}.`);
          broadcastCardToast(`${target.name} was discarded due to ${card.name} effect.`);
        } else {
          addLog(`${card.name} — discard skipped (required by effect).`);
        }
        broadcastCounters();
        render();
        setupDropZones();
      },
      card
    );
  },
});

// GD03-098 "Graham Aker" — Pilot
// [During Link] When this rested Unit is set as active by an effect, choose 1
// enemy Unit with 3 or less HP. Return it to its owner's hand.
// Fired from setRested() (player-initiated) and NET.onUnitRest (opponent-
// driven) whenever a playerField unit transitions rested → active.
// No turn restriction — fires on both the player's and opponent's turns.
registerCardEffect('GD03-098', {
  unrest: (card, ctx) => {
    if (!ctx?.unit) return;
    if (!isLinkUnit(ctx.unit)) return;
    const candidates = state.oppField.filter(c =>
      c && !c.isShield && !c.isDirect && effectiveHp(c) <= 3
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 3 or less HP. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit with 3 or less HP. Return it to its owner\'s hand.',
      candidates,
      (target) => {
        if (target) {
          bounceEnemyUnit(target);
          addLog(isUnitToken(target)
            ? `${card.name} — ${target.name} (token) removed from the game.`
            : `${card.name} — returned ${target.name} to its owner's hand.`);
          render();
          setupDropZones();
        }
      },
      card
    );
  },
});

// GD03-099 "Emma Sheen" — Pilot
// [During Link][Destroyed] If either player has a white Base in their base zone,
// choose 1 enemy Unit whose Lv. is equal to or lower than the unit Emma Sheen
// was linked to. Return it to its owner's hand.
// ctx.ownerSide — set by runDamageStep's pilot-trigger path; guard on it so
// the picker only opens on the controller's client.
// ctx.unit — the destroyed unit+pilot; its level sets the target ceiling.
registerCardEffect('GD03-099', {
  destroyed: (card, ctx) => {
    if (!ctx || ctx.ownerSide !== 'player') return;
    // [During Link] — only fires when the destroyed unit's link requirement
    // matched this pilot. Check ctx.unit.link directly against the pilot card
    // instead of isLinkUnit(ctx.unit), which requires ctx.unit.pilot to be set
    // (it may be cleared by trigger time in some paths).
    const linkAlts = (ctx.unit?.link || '').split('/').map(s => s.replace(/[[\]]/g, '').trim()).filter(Boolean);
    const isLinked = linkAlts.length > 0 && linkAlts.some(alt => {
      if (alt.includes('Trait')) {
        const m = alt.match(/\(([^)]+)\)/);
        return !!m && ((card.trait || '').includes(`(${m[1]})`));
      }
      return alt === card.name || alt === card.pilotName || (card.pilotAliases || []).includes(alt);
    });
    if (!isLinked) return;
    // "friendly white Base" = the controller's own base zone
    const hasWhiteBase = state.playerBase && (state.playerBase.color || '') === 'white';
    if (!hasWhiteBase) {
      addLog(`${card.name} — no white Base in play. No effect.`);
      return;
    }
    const unitLevel = parseInt(ctx.unit?.level) || 0;
    const candidates = state.oppField.filter(c =>
      c && !c.isShield && !c.isDirect && (parseInt(c.level) || 0) <= unitLevel
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — no valid enemy Unit (Lv.${unitLevel} or lower). No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      `Choose 1 enemy Unit whose Lv. is equal to or lower than this Unit (Lv.${unitLevel}). Return it to its owner's hand.`,
      candidates,
      (target) => {
        if (target) {
          bounceEnemyUnit(target);
          addLog(isUnitToken(target)
            ? `${card.name} — ${target.name} (token) removed from the game.`
            : `${card.name} — returned ${target.name} to its owner's hand.`);
          render();
          setupDropZones();
        }
      },
      card
    );
  },
});

// GD03-100 "Soma Peries" — Pilot
// [Destroyed] While paired with a unit — choose 1 enemy Unit. It gets AP-3
// during this turn.
// No [During Link] qualifier: fires for any paired unit, linked or not.
// ctx.ownerSide guards so the picker only opens on the controller's client.
registerCardEffect('GD03-100', {
  destroyed: (card, ctx) => {
    if (!ctx || ctx.ownerSide !== 'player') return;
    const candidates = state.oppField.filter(c => c && !c.isShield && !c.isDirect);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units in play. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. It gets AP-3 during this turn.',
      candidates,
      (target) => {
        if (target) {
          applyApMod([target], -3, 'tempApMod');
          addLog(`${card.name} — ${target.name} gets AP-3 during this turn.`);
          render();
          setupDropZones();
        }
      },
      card
    );
  },
});

// GD04-100 "Sochie Heim" — Pilot
// [Once per Turn] When you pay ① or more cost for one of your Units' effects,
// you may increase this Unit's AP during this turn by an amount equal to the
// cost paid.
// Registers a permanent watcher on unitEffectResSpentWatchers when paired.
// The watcher checks the pilot is still on the field and the once-per-turn
// gate before boosting. unitEffectResSpentWatchers is reset in initGame() so
// watchers from a previous game session never linger.
registerCardEffect('GD04-100', {
  pair: (card) => {
    unitEffectResSpentWatchers.push((side, amount) => {
      if (side !== 'player') return;
      if (card.passiveUsedThisTurn) return;
      // Verify the pilot is still paired with a unit on the field
      const unit = state.playerField.find(c => c && c.pilot?.uid === card.uid);
      if (!unit) return;
      card.passiveUsedThisTurn = true;
      applyApMod([unit], amount, 'tempApMod');
      addLog(`${card.name} — ${unit.name} gets AP+${amount} this turn.`);
      render();
    });
  },
});

// GD04-130 "Industrial 7"
// [Activate･Main][Once per Turn]Exile 1 Command card from your trash:
// Choose 1 enemy Unit. It gets AP-1 during this turn. (The exile-from-trash
// cost itself is already paid generically by ACTIVATE_BASE before this
// trigger fires — see getBaseActivationCost()/showBaseActivateModal in
// js/game.js — this handler only resolves the AP-1 effect.)
registerCardEffect('GD04-130', {
  canActivate: () => state.oppField.some(c => c),
  activate: (card, ctx) => {
    const candidates = state.oppField.filter(c => c);
    pickAndApplyApMod(card, candidates, -1, 'tempApMod', "Choose 1 enemy Unit. It gets AP-1 during this turn.", ctx.onDone);
  },
});

// GD04-129 "Willgem"
// [Deploy] ...Then, deal 3 damage to this Base.
// [Once per Turn] During your turn, when you pay ① or more for a friendly
// Unit's effect, this Base recovers 2 HP. (Registers a permanent watcher on
// notifyUnitEffectResSpent/unitEffectResSpentWatchers — see
// js/effects/registry.js. No code path calls that hook yet, since
// Unit-activated abilities have no dispatch/UI path anywhere in this engine
// today; the watcher will simply never fire until that system is built.)
registerCardEffect('GD04-129', {
  deploy: (card) => {
    dealDamageToBase('player', 3, { fromEnemy: false });
    render();
    setupDropZones();

    unitEffectResSpentWatchers.push((side, amount) => {
      if (side !== 'player' || amount < 1) return;
      if (!state.playerBase || state.playerBase.uid !== card.uid) return;
      if (card.passiveUsedThisTurn) return;
      card.passiveUsedThisTurn = true;
      const entry = getCardDbEntry(card.img);
      const maxHp = entry ? (parseStatValue(entry.hp) ?? 0) : 0;
      const newHp = Math.min((parseInt(card.hp) || 0) + 2, maxHp);
      card.hp = newHp;
      if (newHp >= maxHp) card.damaged = false;
      addLog(`${card.name} recovers 2 HP (HP now: ${newHp}).`);
      render();
    });
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD05 WHITE CARDS
// ════════════════════════════════════════════════════════════════════════════

// ─── GD05-066 · Shining Gundam ───────────────────────────────────────────────
// [Deploy] You may choose 2 (MF) Unit cards from your trash. Exile them from
// the game. If you do, choose 1 (Special Move) Command card from your
// trash. Add it to your hand.
// [Attack][Once per Turn] Choose 1 of your rested Resources. Set it as active.
registerCardEffect('GD05-066', {
  deploy: (card) => {
    const candidates = state.playerTrash.filter(c => (c.type === 'unit' || c.type === 'unit-token') && (c.trait || '').includes('(MF)'));
    if (candidates.length < 2) return;
    showCardPickerModalMulti(
      card.name,
      'You may exile 2 (MF) Unit cards from your trash. If you do, choose 1 (Special Move) Command card from your trash to add to your hand.',
      candidates,
      { min: 2, max: 2, mandatory: false },
      (chosen) => {
        if (!chosen || chosen.length < 2) return;
        exileCardsFromTrash(chosen);
        addLog(`${card.name} — exiled ${chosen.map(c => c.name).join(', ')}.`);
        const cmdCandidates = state.playerTrash.filter(c => (c.type === 'command' || c.type === 'command pilot') && (c.trait || '').includes('(Special Move)'));
        broadcastCounters(); render(); setupDropZones();
        if (cmdCandidates.length === 0) return;
        showCardPickerModal(card.name, 'Choose 1 (Special Move) Command card from your trash. Add it to your hand.', cmdCandidates, (target) => {
          if (!target) return;
          const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
          if (idx !== -1) { state.playerTrash.splice(idx, 1); state.trashCount = Math.max(0, state.trashCount - 1); }
          updateTempState(target.cardNo, target.copyVer, 'hand');
          state.hand.push(target);
          addLog(`${card.name} — added ${target.name} to hand.`);
          glowHandCard(target.uid);
          broadcastCounters(); render(); setupDropZones();
        }, card, { mandatory: false, skipLabel: 'Decline' });
      },
      card,
      { mandatory: false, skipLabel: 'Skip' }
    );
  },
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (card.passiveUsedThisTurn) { ctx.onDone(); return; }
    if (state.res >= state.resBase) { ctx.onDone(); return; }
    card.passiveUsedThisTurn = true;
    state.res = Math.min(state.res + 1, state.resBase);
    state.resourceSetActiveThisTurn = true;
    broadcastCounters();
    addLog(`${card.name} — [Attack][Once per Turn] set 1 rested Resource as active (RES now ${state.res}).`);
    render();
    ctx.onDone();
  },
});

// ─── GD05-067 · Wing Gundam Zero (EW) ────────────────────────────────────────
// While a rested enemy Unit is in play, this Unit gains <Suppression>.
// [Attack] Choose 1 enemy Unit. Rest it.
registerCardEffect('GD05-067', {
  passiveKeywords: (card, ownerSide) => {
    const enemyField = ownerSide === 'player' ? state.oppField : state.playerField;
    return enemyField.some(c => c && c.rested) ? ['Suppression'] : [];
  },
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Rest it.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD05-068 · Shining Gundam (Super Mode) ─────────────────────────────────
// When you activate a (Special Move) Command's [Main]/[Action], this Unit
// gains <Suppression> during this turn — a live check of
// state.specialMoveCommandActivatedThisTurn (mine) /
// state.oppSpecialMoveCommandActivatedThisTurn (opponent's copy of this
// card), rather than a one-time tempKeywords grant, so it automatically
// covers every activation for the rest of the turn without needing to hook
// each one individually.
// [During Link][Attack] This Unit gets AP+2 during this battle.
registerCardEffect('GD05-068', {
  passiveKeywords: (card, ownerSide) =>
    (ownerSide === 'player' ? state.specialMoveCommandActivatedThisTurn : state.oppSpecialMoveCommandActivatedThisTurn) ? ['Suppression'] : [],
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if (!isLinkUnit(card)) { ctx.onDone(); return; }
    applyApMod([card], 2, 'battleApMod');
    addLog(`${card.name} — [During Link][Attack] gets AP+2 this battle.`);
    render();
    ctx.onDone();
  },
});

// ─── GD05-069 · Gundam Maxter ────────────────────────────────────────────────
// During your turn, when this Unit destroys an enemy Unit with battle
// damage, look at the top 4 cards of your deck. You may reveal 1 (Special
// Move) Command card among them and add it to your hand. Return the
// remaining cards randomly to the bottom of your deck.
// [During Link][Attack] Activate [Main] on the card paired with this Unit.
registerCardEffect('GD05-069', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    lookAtTopAndPickToHand(
      card, 4,
      'Look at top 4 cards. Reveal 1 (Special Move) Command card to add to your hand.',
      c => (c.type === 'command' || c.type === 'command pilot') && (c.trait || '').includes('(Special Move)')
    );
  },
  attack: (card, ctx) => activatePairedPilotMain(card, ctx),
});

// ─── GD05-070 · Tallgeese III ────────────────────────────────────────────────
// [Once per Turn] During your turn, when this Unit destroys an enemy Unit
// with battle damage, choose 1 of your rested (Preventer)/(G Team) Link
// Units. Set it as active. It can't attack during this turn.
registerCardEffect('GD05-070', {
  onUnitDestroysEnemy: (card, destroyed, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    if (card.passiveUsedThisTurn) return;
    const candidates = state.playerField.filter(c =>
      c && c.rested && isLinkUnit(c) && ((c.trait || '').includes('(Preventer)') || (c.trait || '').includes('(G Team)'))
    );
    if (candidates.length === 0) return;
    card.passiveUsedThisTurn = true;
    showCardPickerModal(card.name, "Choose 1 of your rested (Preventer)/(G Team) Link Units. Set it as active. It can't attack this turn.", candidates, (target) => {
      if (target) {
        setRested(target, false);
        target.tempCantAttack = true;
        addLog(`${card.name} — set ${target.name} active. Can't attack this turn.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD05-071 · Gundam Sandrock Custom (EW) ─────────────────────────────────
// [Attack] If you have another (G Team)/(Preventer) Unit in play, choose 1
// enemy Unit. It gets AP-2 during this turn.
registerCardEffect('GD05-071', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const hasOther = state.playerField.some(c => c && c.uid !== card.uid && ((c.trait || '').includes('(G Team)') || (c.trait || '').includes('(Preventer)')));
    if (!hasOther) { ctx.onDone(); return; }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { ctx.onDone(); return; }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-2 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 during this turn.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD05-072 · Rising Gundam ────────────────────────────────────────────────
// [When Linked] Choose 1 enemy Unit with 4 or less HP. Rest it.
registerCardEffect('GD05-072', {
  pair: (card, ctx) => {
    if (!ctx?.unit || !isLinkUnit(ctx.unit)) return;
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 4);
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 4 or less HP. Rest it.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD05-073 · Altron Gundam (EW) ───────────────────────────────────────────
// [Deploy] Choose 1 rested enemy Unit. It won't be set as active during the
// start phase of your opponent's next turn.
registerCardEffect('GD05-073', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && c.rested);
    if (candidates.length === 0) {
      addLog(`${card.name} — no rested enemy Units. No effect.`);
      return;
    }
    showCardPickerModal(card.name, "Choose 1 rested enemy Unit. It won't be set active during the opponent's next Start Phase.", candidates, (target) => {
      if (target) {
        target.keepRestedNextTurn = true;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'keepRestedNextTurn' });
        addLog(`${card.name} — ${target.name} won't be set active next turn.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD05-074 · Noin's Taurus ────────────────────────────────────────────────
// [Destroyed] Draw 1. Then, discard 1.
registerCardEffect('GD05-074', {
  destroyed: (card, ctx) => {
    // ctx.ownerSide (not ctx.defenderSide) — see the identical fix/rationale
    // on GD05-050 Gundam Exia Repair: this Unit can die as either attacker
    // or defender, and ctx.defenderSide only describes who's defending in
    // this battle, not who owns the card, so it wrongly skipped this effect
    // whenever Noin's Taurus died as the attacker.
    if (!ctx || ctx.ownerSide !== 'player') return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [Destroyed] drew ${drawn[0].name}.`);
    }
    if (state.hand.length === 0) { broadcastCounters(); render(); setupDropZones(); ctx?.onDone?.(); return; }
    showCardPickerModal(card.name, 'Discard 1 card from your hand.', [...state.hand], (discard) => {
      if (discard) {
        state.hand = state.hand.filter(c => c.uid !== discard.uid);
        updateTempState(discard.cardNo, discard.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(discard));
        broadcastLog(`${card.name} — discarded ${discard.name}.`);
        broadcastCardToast(`${discard.name} was discarded due to ${card.name} effect.`);
      }
      broadcastCounters(); render(); setupDropZones();
      ctx?.onDone?.();
    }, card, { mandatory: true });
  },
});

// ─── GD05-075 · Royal Gundam ─────────────────────────────────────────────────
// <Blocker> (native).
// This Unit can't choose the enemy player as its attack target.
registerCardEffect('GD05-075', {
  cantDirectAttack: () => true,
});

// ─── GD05-076 · Bolt Gundam ──────────────────────────────────────────────────
// [During Link][Attack] Activate [Main] on the card paired with this Unit.
registerCardEffect('GD05-076', {
  attack: (card, ctx) => activatePairedPilotMain(card, ctx),
});

// ─── GD05-078 · Gundam Deathscythe Hell (EW) ────────────────────────────────
// On the turn this Unit is deployed, it may choose a rested enemy Unit as
// its attack target and attack it.
registerCardEffect('GD05-078', {
  deploy: (card) => {
    card.tempAttackDespiteSickness = true;
    card.tempCantAttackNonUnit = true;
    addLog(`${card.name} — [Deploy] may attack a rested enemy Unit this turn despite just being deployed.`);
  },
});

// ─── GD05-079 · Gundam Heavyarms Custom (EW) ────────────────────────────────
// [Activate･Main][Once per Turn] If you have another (G Team)/(Preventer)
// Unit in play, choose 1 enemy Unit that is Lv.4 or lower. It gets AP-1
// during this turn.
registerCardEffect('GD05-079', {
  canActivate: (card) => {
    const hasOther = state.playerField.some(c => c && c.uid !== card.uid && ((c.trait || '').includes('(G Team)') || (c.trait || '').includes('(Preventer)')));
    return hasOther && state.oppField.some(c => c && (parseInt(c.level) || 0) <= 4);
  },
  activate: (card, ctx) => {
    const hasOther = state.playerField.some(c => c && c.uid !== card.uid && ((c.trait || '').includes('(G Team)') || (c.trait || '').includes('(Preventer)')));
    if (!hasOther) {
      addLog(`${card.name} — no other (G Team)/(Preventer) Unit in play. No effect.`);
      ctx.onDone(); return;
    }
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.4 or lower. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit Lv.4 or lower. It gets AP-1 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -1, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-1 during this turn.`);
        render();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD05-097 · Domon Kasshu — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand (generic).
// [When Paired] Draw 1. Then, discard 1. If the discarded card was a
// (Special Move) Command card with a [Main] effect, you may activate its
// [Main] effect.
registerCardEffect('GD05-097', {
  pair: (card) => {
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${card.name} — [When Paired] drew ${drawn[0].name}.`);
    } else {
      addLog(`${card.name} — [When Paired] deck empty, no draw.`);
    }
    if (state.hand.length === 0) { broadcastCounters(); render(); setupDropZones(); return; }
    showCardPickerModal(card.name, 'Discard 1 card from your hand.', [...state.hand], (discard) => {
      if (!discard) { broadcastCounters(); render(); setupDropZones(); return; }
      state.hand = state.hand.filter(c => c.uid !== discard.uid);
      updateTempState(discard.cardNo, discard.copyVer, 'trash');
      state.trashCount++;
      state.playerTrash.push(toTrashCard(discard));
      broadcastLog(`${card.name} — discarded ${discard.name}.`);
      broadcastCardToast(`${discard.name} was discarded due to ${card.name} effect.`);
      const isSpecialMoveMain = (discard.type === 'command' || discard.type === 'command pilot') &&
        (discard.trait || '').includes('(Special Move)') &&
        (discard.effectRaw || '').includes('[Main]');
      broadcastCounters(); render(); setupDropZones();
      if (!isSpecialMoveMain) return;
      showCardPickerModal(card.name, `You may activate ${discard.name}'s [Main] effect.`, [discard], (chosen) => {
        if (!chosen) return;
        const handled = triggerCardEffect(discard, 'main', { onDone: () => fireCommandEffectPassives(discard) });
        if (handled) addLog(`${card.name} — activated ${discard.name}'s [Main] effect.`);
      }, card, { mandatory: false, skipLabel: 'Decline' });
    }, card, { mandatory: true });
  },
});

// ─── GD05-098 · Heero Yuy — Pilot ────────────────────────────────────────────
// [Burst] Add this card to your hand (generic).
// When this Unit destroys an enemy shield area card with damage, choose 1
// enemy Unit. It gets AP-2 during this turn.
registerCardEffect('GD05-098', {
  onLinkedUnitAttackDestroysShield: (pilot, unit) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModal(pilot.name, 'Choose 1 enemy Unit. It gets AP-2 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${pilot.name} — ${target.name} gets AP-2 during this turn.`);
        render(); setupDropZones();
      }
    }, pilot, { mandatory: true });
  },
});

// ─── GD05-099 · Trowa Barton — Pilot ─────────────────────────────────────────
// [Burst] Add this card to your hand (generic).
// During your turn, when this Unit destroys an enemy Unit with battle
// damage, draw 1. Then, discard 1.
registerCardEffect('GD05-099', {
  onLinkedUnitDestroysEnemy: (pilot, unit, destroyedUnit, destroyerSide) => {
    if (destroyerSide !== 'player' || !state.myTurn) return;
    const drawn = drawCardsFromTempDeck(1, true);
    if (drawn.length > 0) {
      state.hand.push(drawn[0]);
      addLog(`${pilot.name} — ${unit.name} destroyed ${destroyedUnit.name}: drew ${drawn[0].name}.`);
    }
    if (state.hand.length === 0) { broadcastCounters(); render(); return; }
    showCardPickerModal(pilot.name, 'Discard 1 card from your hand.', [...state.hand], (discard) => {
      if (discard) {
        state.hand = state.hand.filter(c => c.uid !== discard.uid);
        updateTempState(discard.cardNo, discard.copyVer, 'trash');
        state.trashCount++;
        state.playerTrash.push(toTrashCard(discard));
        broadcastLog(`${pilot.name} — discarded ${discard.name}.`);
        broadcastCardToast(`${discard.name} was discarded due to ${pilot.name} effect.`);
      }
      broadcastCounters(); render(); setupDropZones();
    }, pilot, { mandatory: true });
  },
});

// ─── GD05-100 · Quatre Raberba Winner — Pilot ───────────────────────────────
// [Burst] Add this card to your hand (generic).
// [When Paired] Choose 1 enemy Unit that is Lv.5 or lower. Rest it.
registerCardEffect('GD05-100', {
  pair: (card) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 5);
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.5 or lower. Rest it.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD05-101 · Gavane Goonny — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand (generic).
// [Once per Turn] When you pay ① or more for one of your Unit's effects, if
// this is a (Militia) Unit, it may recover 2 HP.
// unitEffectResSpentWatchers convention (see GD04-100 Sochie Heim above) —
// registered while paired, verifies the pilot is still on the field before
// firing.
registerCardEffect('GD05-101', {
  pair: (card) => {
    unitEffectResSpentWatchers.push((side, amount, unit) => {
      if (side !== 'player' || amount < 1) return;
      if (card.passiveUsedThisTurn) return;
      const stillPaired = state.playerField.some(c => c && c.pilot?.uid === card.uid);
      if (!stillPaired) return;
      if (!unit || !(unit.trait || '').includes('(Militia)')) return;
      card.passiveUsedThisTurn = true;
      showCardPickerModal(card.name, `${unit.name} paid RES for its own effect. It may recover 2 HP.`, [unit], (chosen) => {
        if (chosen) {
          applyHpMod([chosen], 2);
          addLog(`${card.name} — ${unit.name} recovers 2 HP.`);
          render();
        }
      }, card, { mandatory: false, skipLabel: 'Decline' });
    });
  },
});

// ─── GD05-118 · Incendiary Spark — Command ──────────────────────────────────
// [Main] Choose 1 enemy Unit. It gets AP-2 during this turn. If you use an
// EX Resource to play this card, rest the enemy Unit.
// Per ruling: ask the player (via a choice modal, only if they actually
// have an active EX Resource) whether to deliberately pay with an EX
// Resource BEFORE the enemy-Unit picker opens. card._preferExPayment tells
// ACTIVATE_COMMAND's spendResources() call to drain exRes first instead of
// last; card._usedExResThisPlay records the player's choice for the [Main]
// handler to read (not just "did exRes happen to get touched").
registerCardEffect('GD05-118', {
  prePlayFromHand: (card, slot, onProceed) => {
    if (state.exRes <= 0) { card._usedExResThisPlay = false; onProceed(); return; }
    showChooseOneEffectModal(card, [
      {
        label: 'Pay with a normal Resource.',
        run: (done) => { card._usedExResThisPlay = false; card._preferExPayment = false; done(); onProceed(); },
      },
      {
        label: 'Pay with an EX Resource instead (also rests the chosen enemy Unit).',
        run: (done) => { card._usedExResThisPlay = true; card._preferExPayment = true; done(); onProceed(); },
      },
    ], () => {});
  },
  main: (card, ctx) => {
    const usedEx = !!card._usedExResThisPlay;
    card._usedExResThisPlay = false;
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) { addLog(`${card.name} — no enemy Units. No effect.`); ctx.onDone(); return; }
    showCardPickerModal(
      card.name,
      'Choose 1 enemy Unit. It gets AP-2 during this turn.' + (usedEx ? ' It will also be rested (paid with an EX Resource).' : ''),
      candidates,
      (target) => {
        if (target) {
          applyApMod([target], -2, 'tempApMod');
          addLog(`${card.name} — ${target.name} gets AP-2 during this turn.`);
          if (usedEx) {
            setRested(target, true);
            addLog(`${card.name} — paid with an EX Resource: also rested ${target.name}.`);
          }
          render(); setupDropZones();
        }
        ctx.onDone();
      },
      card,
      { mandatory: true }
    );
  },
});

// ─── GD05-119 · A Wind Against Fires — Command Pilot ────────────────────────
// [Action] Choose 1 enemy Unit that is battling one of your Units that is
// Lv.5 or higher. It gets AP-3 during this battle.
// [Pilot] Zechs Merquise
function gd05119BattlingEnemyUnit(battle) {
  if (!battle) return null;
  if (battle.attackerSide === 'player' && battle.attacker && (parseInt(battle.attacker.level) || 0) >= 5) {
    const d = battle.defender;
    if (d && !d.isShield && !d.isDirect && (d.type === 'unit' || d.type === 'unit-token')) return d;
  }
  if (battle.defenderSide === 'player' && battle.defender && !battle.defender.isShield && !battle.defender.isDirect &&
      (parseInt(battle.defender.level) || 0) >= 5) {
    const a = battle.attacker;
    if (a && (a.type === 'unit' || a.type === 'unit-token')) return a;
  }
  return null;
}
registerCardEffect('GD05-119', {
  canActivate: (card, ctx) => !!gd05119BattlingEnemyUnit(ctx),
  action: (card, ctx) => {
    const battle = ctx.battle || ctx;
    const target = gd05119BattlingEnemyUnit(battle);
    if (!target) {
      addLog(`${card.name} — no qualifying battling enemy Unit.`);
      ctx.onDone(); return;
    }
    applyApMod([target], -3, 'battleApMod');
    addLog(`${card.name} — ${target.name} gets AP-3 this battle.`);
    render(); setupDropZones();
    ctx.onDone();
  },
});

// ─── GD05-120 · Shining Finger — Command ─────────────────────────────────────
// [Burst] Add this card to your hand (generic).
// [Main]/[Action] Choose 1 enemy Unit with 4 or less HP. Rest it. Then, you
// may choose 1 of your Units with "Shining Gundam" in its card name. It
// gets <First Strike> during this turn.
function gd05120Effect(card, onDone) {
  const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 4);
  if (candidates.length === 0) { addLog(`${card.name} — no enemy Units with 4 or less HP. No effect.`); onDone(); return; }
  showCardPickerModal(card.name, 'Choose 1 enemy Unit with 4 or less HP. Rest it.', candidates, (target) => {
    if (target) {
      setRested(target, true);
      addLog(`${card.name} — rested ${target.name}.`);
    }
    const shiningCandidates = state.playerField.filter(c => c && (c.name || '').includes('Shining Gundam'));
    if (shiningCandidates.length === 0) { render(); setupDropZones(); onDone(); return; }
    showCardPickerModal(card.name, 'You may choose 1 of your Units with "Shining Gundam" in its name. It gets <First Strike> during this turn.', shiningCandidates, (fsTarget) => {
      if (fsTarget) {
        fsTarget.tempKeywords = fsTarget.tempKeywords || [];
        if (!fsTarget.tempKeywords.includes('First Strike')) fsTarget.tempKeywords.push('First Strike');
        NET.send({ type: 'TEMP_KEYWORD_ADD', uid: fsTarget.uid, keyword: 'First Strike' });
        addLog(`${card.name} — ${fsTarget.name} gains <First Strike> this turn.`);
      }
      render(); setupDropZones();
      onDone();
    }, card, { mandatory: false, skipLabel: 'Decline' });
  }, card, { mandatory: true });
}
registerCardEffect('GD05-120', {
  canActivate: () => state.oppField.some(c => c && effectiveHp(c) <= 4),
  main: (card, ctx) => gd05120Effect(card, ctx.onDone),
  action: (card, ctx) => gd05120Effect(card, ctx.onDone),
});

// ─── GD05-121 · Cyclone Punch — Command Pilot ───────────────────────────────
// [Main] Choose 1 enemy Unit. It gets AP-2 during this turn. After
// activating this card's [Main], you may pair this card from your trash
// with one of your (MF) Units.
// [Pilot] Chibodee Crocket
registerCardEffect('GD05-121', {
  canActivate: () => state.oppField.some(c => c),
  main: (card, ctx) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units. No effect.`);
      offerPairFromTrash(card, '(MF)', ctx.onDone);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-2 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 during this turn.`);
        render();
      }
      offerPairFromTrash(card, '(MF)', ctx.onDone);
    }, card, { mandatory: true });
  },
});

// ─── GD05-122 · Graviton Hammer — Command Pilot ─────────────────────────────
// [Main] Choose 1 enemy Unit that is Lv.4 or lower. Rest it. After
// activating this card's [Main], you may pair this card from your trash
// with one of your (MF) Units.
// [Pilot] Argo Gulskii
registerCardEffect('GD05-122', {
  canActivate: () => state.oppField.some(c => c && (parseInt(c.level) || 0) <= 4),
  main: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.4 or lower. No effect.`);
      offerPairFromTrash(card, '(MF)', ctx.onDone);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit Lv.4 or lower. Rest it.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render(); setupDropZones();
      }
      offerPairFromTrash(card, '(MF)', ctx.onDone);
    }, card, { mandatory: true });
  },
});

// ─── GD05-130 · Presidential Office — Base ──────────────────────────────────
// [Burst]/[Deploy] handled generically (deploy this card / shield to hand).
// [Destroyed] You may exile this card in your trash from the game. If you
// do, you may deploy 1 Base card with "Presidential Office" in its card
// name from your hand (a normal DEPLOY_BASE, so it still pays its own cost).
registerCardEffect('GD05-130', {
  destroyed: (card, ctx) => {
    if (ctx?.defenderSide === 'opp') return;
    showCardPickerModal(card.name, 'You may exile this card from your trash.', [card], (chosen) => {
      if (!chosen) { ctx?.onDone?.(); return; }
      exileCardsFromTrash([card]);
      addLog(`${card.name} — exiled from trash.`);
      const candidates = state.hand.filter(c => c.type === 'base' && (c.name || '').includes('Presidential Office'));
      broadcastCounters(); render(); setupDropZones();
      if (candidates.length === 0 || state.playerBase) { ctx?.onDone?.(); return; }
      showCardPickerModal(card.name, 'You may deploy 1 Base card with "Presidential Office" in its name from your hand.', candidates, (baseCard) => {
        if (baseCard) dispatch('DEPLOY_BASE', { uid: baseCard.uid });
        ctx?.onDone?.();
      }, card, { mandatory: false, skipLabel: 'Decline' });
    }, card, { mandatory: false, skipLabel: 'Decline' });
  },
});

// ─── ST14-005 · G Falcon DX ──────────────────────────────────────────────────
// [Deploy] Choose 1 enemy Unit that is Lv.6 or lower. Rest it. Then, if there
// are 7 or more cards in your trash, that unit gets AP-2 during this turn.
registerCardEffect('ST14-005', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 6);
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, 'Choose 1 enemy Unit (Lv.6 or lower). Rest it.', candidates, (target) => {
      if (!target) return;
      setRested(target, true);
      addLog(`${card.name} — rested ${target.name}.`);
      if (state.trashCount >= 7) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${card.name} — 7+ cards in trash: ${target.name} gets AP-2 during this turn.`);
      }
      render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// ─── ST14-014 · Garrod Ran & Tiffa Adill ─────────────────────────────────────
// [Main]/[Action] Choose 1 enemy Unit that is Lv.5 or lower. It gets AP-3
// during this turn. If there are 4 or more Command cards in your trash,
// choose 1 enemy Unit instead (no level restriction).
function st14014Effect(card, onDone) {
  const noLevelLimit = state.playerTrash.filter(c => c.type === 'command' || c.type === 'command pilot').length >= 4;
  const candidates = state.oppField.filter(c => c && (noLevelLimit || (parseInt(c.level) || 0) <= 5));
  if (candidates.length === 0) {
    addLog(`${card.name} — no valid enemy Unit target. No effect.`);
    onDone(); return;
  }
  showCardPickerModal(
    card.name,
    noLevelLimit
      ? 'Choose 1 enemy Unit. It gets AP-3 during this turn.'
      : 'Choose 1 enemy Unit that is Lv.5 or lower. It gets AP-3 during this turn.',
    candidates,
    (target) => {
      if (target) {
        applyApMod([target], -3, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-3 during this turn.`);
        render(); setupDropZones();
      }
      onDone();
    },
    card, { mandatory: true }
  );
}
registerCardEffect('ST14-014', {
  main:   (card, ctx) => st14014Effect(card, ctx.onDone),
  action: (card, ctx) => st14014Effect(card, ctx.onDone),
});

// ST14-004 "Zowort" — Unit
// Passive: this Unit cannot choose the enemy player (base / shield / direct)
// as its attack target — only enemy Units are valid attack targets.
registerCardEffect('ST14-004', {
  cantAttackNonUnit: () => true,
});

// ─── ST14-013 · Natural Talent — Command ─────────────────────────────────────
// [Burst] Choose 1 enemy Unit. It gets AP-3 during this turn.
// [Main][Action] When playing this card, choose 1 of the following effects
// and activate it:
// ■ Choose 1 to 2 enemy Units with 3 or less HP. Rest them.
// ■ Choose 1 enemy Unit. It gets AP-3 during this turn.
// showChooseOneEffectModal is defined in blue.js (GD05-102) — all effect
// files share one global scope, so it's reused here rather than duplicated.
function st14013MainEffect(card, onDone) {
  showChooseOneEffectModal(card, [
    {
      label: 'Choose 1 to 2 enemy Units with 3 or less HP. Rest them.',
      run: (done) => {
        const candidates = state.oppField.filter(c => c && !c.rested && effectiveHp(c) <= 3);
        if (candidates.length === 0) {
          addLog(`${card.name} — no active enemy Units with 3 or less HP. No effect.`);
          done(); return;
        }
        showCardPickerModalMulti(
          card.name,
          'Choose 1 to 2 active enemy Units with 3 or less HP. Rest them.',
          candidates,
          { min: 1, max: 2, mandatory: true },
          (targets) => {
            if (targets && targets.length > 0) {
              targets.forEach(t => { setRested(t, true); addLog(`${card.name} — ${t.name} rested.`); });
              render(); setupDropZones();
            }
            done();
          },
          card
        );
      },
    },
    {
      label: 'Choose 1 enemy Unit. It gets AP-3 during this turn.',
      run: (done) => {
        const candidates = state.oppField.filter(c => c);
        pickAndApplyApMod(card, candidates, -3, 'tempApMod', 'Choose 1 enemy Unit. It gets AP-3 during this turn.', done);
      },
    },
  ], onDone);
}
registerCardEffect('ST14-013', {
  burst: (card, ctx) => {
    const candidates = state.oppField.filter(c => c);
    pickAndApplyApMod(card, candidates, -3, 'tempApMod', 'Choose 1 enemy Unit. It gets AP-3 during this turn.', () => ctx?.onDone?.());
  },
  main:   (card, ctx) => st14013MainEffect(card, ctx.onDone),
  action: (card, ctx) => st14013MainEffect(card, ctx.onDone),
});

// ─── GD06-069 · SUMO Silver Type ────────────────────────────────────────────
// <Blocker>
// [Activate･Action][Once per Turn]①: Choose 1 of your damaged (Dianna
// Counter) Units. It recovers 1 HP.
// Once-per-turn is handled generically (activateActionUsedThisTurn, checked
// by getActionEligibleUnits); the ① RES cost has no generic Activate·Action
// payment (see GD05-022's comment), so it's checked/spent by hand here.
registerCardEffect('GD06-069', {
  canAction: () => {
    if ((state.res + state.exRes) < 1) return false;
    return state.playerField.some(c => c && c.damaged && (c.trait || '').includes('(Dianna Counter)'));
  },
  action: (card, ctx) => {
    const candidates = state.playerField.filter(c => c && c.damaged && (c.trait || '').includes('(Dianna Counter)'));
    if (candidates.length === 0) {
      addLog(`${card.name} — no damaged (Dianna Counter) Units. No effect.`);
      ctx.onDone();
      return;
    }
    spendResources(1);
    broadcastCounters();
    showCardPickerModal(card.name, 'Choose 1 of your damaged (Dianna Counter) Units. It recovers 1 HP.', candidates, (target) => {
      if (target) {
        applyHpMod([target], 1);
        addLog(`${card.name} — paid ①: ${target.name} recovers 1 HP.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD06-071 · Shining Gundam (Meikyo Shisui) ──────────────────────────────
// [Once per Turn] During your turn, when you activate a (Special Move)
// Command's [Main]/[Action], choose 1 enemy Unit. It gets AP-2 during this
// turn. If you do, this Unit gets AP+2.
// onFriendlySpecialMoveCommandActivated (game.js's triggerCardEffect) fires
// on every friendly field unit each time ANY (Special Move) Command's
// [Main]/[Action] activates this turn — passiveUsedThisTurn is this card's
// own once-per-turn gate, only consumed once an enemy target actually exists
// to choose (no target = nothing to activate, use not spent).
registerCardEffect('GD06-071', {
  onFriendlySpecialMoveCommandActivated: (card) => {
    if (!state.myTurn || card.passiveUsedThisTurn) return;
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) return;
    card.passiveUsedThisTurn = true;
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-2 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -2, 'tempApMod');
        applyApMod([card], 2, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-2 during this turn. ${card.name} gets AP+2 during this turn.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-118 · Infinite Variations ─────────────────────────────────────────
// [Main]/[Action] Choose a number of enemy Units up to the number of white
// Units with <Blocker> you have in play. They get AP-2 during this turn.
// Multi-select "up to N" variant of pickAndApplyApMod (which is single-
// target only) — showCardPickerModalMulti with {min:0, max:count} + the
// lower-level applyApMod() for the actual (mirrored) mutation, same shape
// as GD01-119/EB01-083's shared main/action registration.
function gd06118Effect(card, onDone) {
  const count = state.playerField.filter(c => c && c.color === 'white' && hasNativeKeyword(c, 'Blocker')).length;
  const candidates = state.oppField.filter(c => c);
  if (count === 0 || candidates.length === 0) { onDone(); return; }
  showCardPickerModalMulti(
    card.name,
    `Choose up to ${count} enemy Unit${count !== 1 ? 's' : ''}. They get AP-2 during this turn.`,
    candidates,
    { min: 0, max: count },
    (chosen) => {
      if (chosen.length > 0) {
        applyApMod(chosen, -2, 'tempApMod');
        addLog(`${card.name} — ${chosen.map(c => c.name).join(', ')} get AP-2 during this turn.`);
        render();
      }
      onDone();
    },
    card
  );
}
registerCardEffect('GD06-118', {
  canActivate: () => state.playerField.some(c => c && c.color === 'white' && hasNativeKeyword(c, 'Blocker')),
  main:   (card, ctx) => gd06118Effect(card, ctx.onDone),
  action: (card, ctx) => gd06118Effect(card, ctx.onDone),
});

// ─── GD06-130 · Soleil — Base ────────────────────────────────────────────────
// [Burst] Deploy this card. [Deploy] Add 1 of your Shields to your hand —
// both handled generically by deployBaseFromShield()/applyBaseDeployEffect()
// in js/game.js (see ST11-016's identical comment). Then, choose 1 enemy
// Unit with no paired Pilot. Reduce its AP by an amount equal to the number
// of friendly (Dianna Counter)/(Militia) Units during this turn.
registerCardEffect('GD06-130', {
  deploy: (card) => {
    const friendlyCount = state.playerField.filter(c =>
      c && ((c.trait || '').includes('(Dianna Counter)') || (c.trait || '').includes('(Militia)'))
    ).length;
    if (friendlyCount === 0) {
      addLog(`${card.name} — no friendly (Dianna Counter)/(Militia) Units in play. No effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c && !c.pilot);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with no paired Pilot. No effect.`);
      return;
    }
    showCardPickerModal(card.name, `Choose 1 enemy Unit with no paired Pilot. It gets AP-${friendlyCount} during this turn.`, candidates, (target) => {
      if (target) {
        applyApMod([target], -friendlyCount, 'tempApMod');
        addLog(`${card.name} — ${target.name} gets AP-${friendlyCount} during this turn.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD06 batch — "implement all remaining effects" pass
// ════════════════════════════════════════════════════════════════════════════

// ─── EB01-044 · Justice Gundam (EX) — Unit ─────────────────────────────────
// <Blocker> (native). [Deploy] If there are 2 or more enemy players, choose
// 1 Unit belonging to an enemy player with the most Units. Return it to its
// owner's hand.
// This app is a strict 1v1 match, so enemyPlayerCount() is always 1 and this
// branch never fires — see enemyPlayerCount()'s declaration in game.js. The
// bounce logic is still implemented for correctness/documentation (same
// convention as EB01-040 Gundam Epyon, green.js); "most Units" selection is
// unreachable in 1v1 anyway (only one enemy exists).
registerCardEffect('EB01-044', {
  deploy: (card) => {
    if (enemyPlayerCount() < 2) {
      addLog(`${card.name} — [Deploy] fewer than 2 enemy players (1v1 match). No effect.`);
      return;
    }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) return;
    showCardPickerModal(card.name, "Choose 1 Unit belonging to the enemy player with the most Units. Return it to its owner's hand.", candidates, (target) => {
      if (target) {
        bounceEnemyUnit(target);
        addLog(`${card.name} — returned ${target.name} to its owner's hand.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── EB01-058 · Extreme Gundam — Unit ──────────────────────────────────────
// If there are 2 or more enemy players, this Unit gains <Blocker>.
// Always false in 1v1 — see enemyPlayerCount()'s declaration in game.js.
registerCardEffect('EB01-058', {
  passiveKeywords: () => enemyPlayerCount() >= 2 ? ['Blocker'] : [],
});

// ─── EB01-055 · Dom Gross Beil — Unit ───────────────────────────────────────
// If there are 2 or more enemy players and this Unit is rested, friendly
// Shields can't receive battle damage from enemy Units.
// Always false in 1v1 (enemyPlayerCount() is always 1) — no
// shield-battle-damage-immunity mechanism exists in the engine (nothing else
// needs one), so this is registered as an honest permanent no-op rather than
// building unreachable plumbing for a condition that can never be true here.
registerCardEffect('EB01-055', {
  passiveKeywords: () => [],
});

// ─── ST14-003 · Palace Athene — Unit ────────────────────────────────────────
// [Deploy] Choose 1 enemy player. That player choose 2 Unit cards from their
// trash. Exile them from the game.
// requestEnemyTrashUnitExile (game.js) — same new REQUEST_TRASH_UNIT_EXILE/
// TRASH_UNIT_EXILE_DONE round trip as ST13-009 Gundam Pharact (red.js).
registerCardEffect('ST14-003', {
  deploy: (card) => {
    requestEnemyTrashUnitExile(2, () => {});
  },
});

// ─── ST14-001 · The-O — Unit ────────────────────────────────────────────────
// <Suppression> (native). Each enemy player's rested Units with the lowest
// Lv. won't be set as active during the start phase of their turn.
// preventsLowestLevelUnrest hook — wired into registerAction('UNREST_ALL', ...)
// in game.js specifically for this card; see its declaration there.
registerCardEffect('ST14-001', {
  preventsLowestLevelUnrest: () => true,
});

// ─── ST14-011 · Paptimus Scirocco — Pilot ──────────────────────────────────
// [Attack] Choose 1 enemy Unit. Reduce its AP during this battle by an
// amount equal to the number of rested enemy Units.
registerCardEffect('ST14-011', {
  attack: (card, ctx) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Attack] no enemy Units. No effect.`);
      ctx.onDone?.();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. Reduce its AP during this battle by the number of rested enemy Units.', candidates, (target) => {
      if (target) {
        const restedCount = state.oppField.filter(c => c && c.rested).length;
        if (restedCount > 0) {
          applyApMod([target], -restedCount, 'battleApMod');
          addLog(`${card.name} — ${target.name} gets AP-${restedCount} during this battle (${restedCount} rested enemy Units).`);
          render(); setupDropZones();
        } else {
          addLog(`${card.name} — no rested enemy Units. No AP reduction.`);
        }
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
});

// ─── GD06-120 · Hoping for Peace — Command ─────────────────────────────────
// [Burst] Choose 1 enemy Unit with 5 or less HP. Rest it.
// [Main]/[Action] Choose 1 enemy Unit. Rest it.
function gd06120MainAction(card, ctx) {
  const candidates = state.oppField.filter(c => c);
  if (candidates.length === 0) {
    addLog(`${card.name} — no enemy Units. No effect.`);
    ctx.onDone();
    return;
  }
  showCardPickerModal(card.name, 'Choose 1 enemy Unit. Rest it.', candidates, (target) => {
    if (target) {
      setRested(target, true);
      addLog(`${card.name} — rested ${target.name}.`);
      render(); setupDropZones();
    }
    ctx.onDone();
  }, card, { mandatory: true });
}
registerCardEffect('GD06-120', {
  canActivate: () => state.oppField.some(c => c),
  burst: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Burst] no enemy Units with 5 or less HP. No effect.`);
      ctx?.onDone?.();
      return;
    }
    showCardPickerModal(card.name, '[Burst] Choose 1 enemy Unit with 5 or less HP. Rest it.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — [Burst] rested ${target.name}.`);
        render(); setupDropZones();
      }
      ctx?.onDone?.();
    }, card, { mandatory: true });
  },
  main: gd06120MainAction,
  action: gd06120MainAction,
});

// ─── GD06-072 · Gundam Deathscythe Hell (EW) — Unit ────────────────────────
// [During Pair][Destroyed] If you have another (G Team)/(Preventer) Unit in
// play, you may choose 1 Command card that is Lv.5 or lower from your trash.
// Add it to your hand.
// [During Pair]: card.pilot truthy check (not isLinkUnit) — any pairing
// counts, same convention as GD01-026 Char's Zaku II Lv3.
registerCardEffect('GD06-072', {
  destroyed: (card, ctx) => {
    const ownerSide = destroyedOwnerSide(card, ctx);
    if (ownerSide !== 'player') return;
    if (!card.pilot) return; // [During Pair]
    const hasOtherGTeamOrPreventer = state.playerField.some(c =>
      c && c.uid !== card.uid && ((c.trait || '').includes('(G Team)') || (c.trait || '').includes('(Preventer)'))
    );
    if (!hasOtherGTeamOrPreventer) return;
    const candidates = state.playerTrash.filter(c =>
      (c.type === 'command' || c.type === 'command pilot') && (parseInt(c.level) || 0) <= 5
    );
    if (candidates.length === 0) {
      addLog(`${card.name} — [During Pair][Destroyed] no Command card Lv.5 or lower in trash. No effect.`);
      return;
    }
    showCardPickerModal(
      card.name,
      'You may choose 1 Command card that is Lv.5 or lower from your trash. Add it to your hand.',
      candidates,
      (target) => {
        if (!target) return;
        const idx = state.playerTrash.findIndex(c => c.uid === target.uid);
        if (idx !== -1) {
          state.playerTrash.splice(idx, 1);
          state.trashCount = Math.max(0, state.trashCount - 1);
        }
        updateTempState(target.cardNo, target.copyVer, 'hand');
        state.hand.push(target);
        addLog(`${card.name} — [During Pair][Destroyed] recovered ${target.name} from trash to hand.`);
        broadcastCardToast(`${target.name} was added from trash to hand due to ${card.name} effect.`);
        glowHandCard(target.uid);
        broadcastCounters(); render(); setupDropZones();
      },
      card
    );
  },
});

// ─── GD06-098 · Duo Maxwell — Pilot ─────────────────────────────────────────
// [During Link] While this Unit is rested, friendly Bases can't receive
// damage from enemy Units whose Lv. are equal to or lower than this Unit.
// pilotBaseBattleImmunityFrom — new hook checked by hasPilotBaseBattleImmunity
// (game.js) at both base battle-damage-immunity call sites in runDamageStep,
// since a pilot's grant (unlike a Base's own isImmuneToBattleDamageFrom) has
// to be found by scanning every field unit's paired pilot.
registerCardEffect('GD06-098', {
  pilotBaseBattleImmunityFrom: (pilot, unit, attacker) => {
    if (!isLinkUnit(unit) || !unit.rested) return false;
    return (parseInt(attacker.level) || 0) <= (parseInt(unit.level) || 0);
  },
});

// ─── GD06-073 · Wing Gundam Zero (EW) — Unit ───────────────────────────────
// [During Pair][Once per Turn] When this Unit destroys an enemy shield area
// card with battle damage, choose 1 enemy Unit that is Lv.3 or lower. Rest it.
// onAttackDestroysShield — same hook family as ST03-001 Sinanju (red.js).
registerCardEffect('GD06-073', {
  onAttackDestroysShield: (card) => {
    if (!card.pilot) return; // [During Pair]
    if (card.passiveUsedThisTurn) return; // [Once per Turn]
    card.passiveUsedThisTurn = true;
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — [During Pair] shield destroyed: no enemy Units Lv.3 or lower. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.3 or lower. Rest it.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-121 · Crybaby Poe — Command ──────────────────────────────────────
// [Action] Choose 1 enemy Unit. It gets AP-2 during this battle. If it has
// <High-Maneuver>, AP-6 instead.
// Reactive Action-only card (no printed Burst) — canActivate gated to an
// active enemy attack in progress, same convention as EB01-077 Master League
// Begins/GD06-117 Veterans Joining Forces' redirect-style Action commands.
registerCardEffect('GD06-121', {
  canActivate: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'opp') return false;
    return state.oppField.some(c => c);
  },
  action: (card, ctx) => {
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units. No effect.`);
      ctx.onDone();
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-2 during this battle (AP-6 instead if it has <High-Maneuver>).', candidates, (target) => {
      if (target) {
        const delta = hasNativeKeyword(target, 'High-Maneuver') ? -6 : -2;
        applyApMod([target], delta, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP${delta} during this battle.`);
        render(); setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ════════════════════════════════════════════════════════════════════════════
// ST11-14 batch — newly added cards, "implement all remaining effects" pass
// ════════════════════════════════════════════════════════════════════════════

// ─── ST14-016 · Gryps 2 — Base ──────────────────────────────────────────────
// [Burst] Deploy this card. (generic)
// [Deploy] Add 1 of your Shields to your hand. (generic)
// [Once per Turn] When a friendly Unit links, choose 1 Lv.5 or lower enemy
// Unit. It gets AP-1 during this turn.
// onPlayerUnitLinked — same base hook as GD05-127 Girty Lue (red.js).
registerCardEffect('ST14-016', {
  onPlayerUnitLinked: (base) => {
    if (base.passiveUsedThisTurn) return;
    base.passiveUsedThisTurn = true;
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 5);
    if (candidates.length === 0) {
      addLog(`${base.name} — no enemy Units Lv.5 or lower. No effect.`);
      return;
    }
    showCardPickerModal(base.name, 'Choose 1 enemy Unit that is Lv.5 or lower. It gets AP-1 during this turn.', candidates, (target) => {
      if (target) {
        applyApMod([target], -1, 'tempApMod');
        addLog(`${base.name} — ${target.name} gets AP-1 during this turn.`);
        render(); setupDropZones();
      }
    }, base, { mandatory: true });
  },
});

// ─── GD06-067 · Gundam Sandrock Custom (EW) — Unit ──────────────────────────
// <Blocker> (native, set on the DB entry's nativeKeyword — was missing and
// has been added there).
// [When Paired・Lv.4 or Higher Pilot] Choose 1 rested enemy Unit with 3 or
// less HP. It won't be set as active during the start phase of your
// opponent's next turn.
// Registered on the unit itself — PAIR_PILOT fires 'pair' on both the pilot
// AND the paired unit (see e.g. ST05-007 McGillis' Schwalbe Graze above), so
// card.pilot is already set to the just-paired Pilot inside this handler.
// Same keepRestedNextTurn grant as GD05-073 Altron Gundam (EW) (blue.js).
registerCardEffect('GD06-067', {
  pair: (card) => {
    if (!card.pilot || (parseInt(card.pilot.level) || 0) < 4) return;
    const candidates = state.oppField.filter(c => c && c.rested && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — [When Paired・Lv.4+ Pilot] no rested enemy Units with 3 or less HP.`);
      return;
    }
    showCardPickerModal(card.name, "Choose 1 rested enemy Unit with 3 or less HP. It won't be set active during the opponent's next Start Phase.", candidates, (target) => {
      if (target) {
        target.keepRestedNextTurn = true;
        NET.send({ type: 'FIELD_AP_MOD', uids: [target.uid], delta: 1, field: 'keepRestedNextTurn' });
        addLog(`${card.name} — [When Paired] ${target.name} won't be set active next turn.`);
        render(); setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-068 · Gundam Heavyarms Custom (EW) — Unit ─────────────────────────
// [Deploy] Choose 1 enemy Unit that is Lv.3 or lower. Rest it. If you have
// another (G Team)/(Preventer) Unit in play, that enemy Unit gets AP-2
// during this turn.
registerCardEffect('GD06-068', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Deploy] no enemy Units Lv.3 or lower. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.3 or lower. Rest it.', candidates, (target) => {
      if (!target) return;
      setRested(target, true);
      addLog(`${card.name} — [Deploy] rested ${target.name}.`);
      const hasOtherGTeamOrPreventer = state.playerField.some(c => c && c.uid !== card.uid &&
        ((c.trait || '').includes('(G Team)') || (c.trait || '').includes('(Preventer)')));
      if (hasOtherGTeamOrPreventer) {
        applyApMod([target], -2, 'tempApMod');
        addLog(`${card.name} — another (G Team)/(Preventer) Unit in play: ${target.name} gets AP-2 during this turn.`);
      }
      render(); setupDropZones();
    }, card, { mandatory: true });
  },
});

// ─── GD06-076 · WaDom — Unit ─────────────────────────────────────────────────
// [Deploy] Choose 1 enemy Unit with 4 or less HP. Rest it.
registerCardEffect('GD06-076', {
  deploy: (card) => {
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 4);
    if (candidates.length === 0) {
      addLog(`${card.name} — [Deploy] no enemy Units with 4 or less HP. No effect.`);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 4 or less HP. Rest it.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — [Deploy] rested ${target.name}.`);
        render();
        setupDropZones();
      }
    }, card, { mandatory: true });
  },
});

// ─── GD06-097 · Harry Ord — Pilot ──────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// [Once per Turn] When this Unit activates <Blocker>, set it as active.
// onLinkedUnitBlockerActivated — new hook fired from applyBlockDecision
// (game.js) right after a Blocker rests itself to redirect an attack, only
// on the blocking player's own client. "this Unit" = the Unit this Pilot is
// paired with, same convention as GD05-099 Trowa Barton's plain (non-Link-
// gated) "this Unit" pilot passives — no isLinkUnit check.
registerCardEffect('GD06-097', {
  onLinkedUnitBlockerActivated: (pilot, unit) => {
    if (pilot.passiveUsedThisTurn) return;
    pilot.passiveUsedThisTurn = true;
    setRested(unit, false);
    addLog(`${pilot.name} — [Once per Turn] ${unit.name} activated <Blocker>: set it as active.`);
    render();
    setupDropZones();
  },
});

// ─── GD06-065 · SUMO Gold Type — Unit ──────────────────────────────────────
// <Blocker> (native).
// [Activate･Main][Once per Turn]④: Set all your (Dianna Counter)/(Militia)
// Units with <Blocker> as active. They can't attack this turn.
// RES cost + Once-per-Turn gate parsed generically by getUnitActivationCost()
// (game.js), same convention as ST13-001 Qubeley. tempCantAttack is set the
// same way every other "set active, can't attack this turn" effect in this
// codebase does (e.g. GD04-071) — no dedicated network message for it; it
// just rides the next broadcastCounters() full-state sync like those do.
function gd06065BlockerCandidates() {
  return state.playerField.filter(c =>
    c && hasNativeKeyword(c, 'Blocker') &&
    ((c.trait || '').includes('(Dianna Counter)') || (c.trait || '').includes('(Militia)'))
  );
}
registerCardEffect('GD06-065', {
  canActivate: () => gd06065BlockerCandidates().length > 0,
  activate: (card, ctx) => {
    const targets = gd06065BlockerCandidates();
    if (targets.length === 0) { ctx.onDone?.(); return; }
    targets.forEach(t => {
      setRested(t, false);
      t.tempCantAttack = true;
    });
    addLog(`${card.name} — [Activate･Main] set ${targets.map(t => t.name).join(', ')} active. They can't attack this turn.`);
    broadcastCounters();
    render();
    setupDropZones();
    ctx.onDone?.();
  },
});

// ─── GD06-070 · Eagail — Unit ───────────────────────────────────────────────
// [Deploy] Deploy 1 [Gozzo]((Dianna Counter)･AP0･HP3･<Blocker>) Unit token
// (T-030 — synthetic token, not a real printed card).
// [Attack] If you have paid a total of ③ or more for your Units' effects
// this turn, choose 1 enemy Unit. It gets AP-5 during this battle.
// state.playerUnitEffectResSpentThisTurn — running total accumulated in
// showUnitActivateModal (game.js) every time a Unit's own [Activate･Main]/
// [Activate･Action] ability costs RES, reset in CLEAR_TURN_EFFECTS.
registerCardEffect('GD06-070', {
  deploy: (card) => {
    const token = deployTokenToField('T-030');
    if (token) {
      addLog(`${card.name} — [Deploy] deployed 1 ${token.name} Unit token.`);
    } else {
      addLog(`${card.name} — [Deploy] field is full, could not deploy Gozzo token.`);
    }
  },
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    if ((state.playerUnitEffectResSpentThisTurn || 0) < 3) {
      addLog(`${card.name} — fewer than ③ paid for Units' effects this turn. No effect.`);
      ctx.onDone?.(); return;
    }
    const candidates = state.oppField.filter(c => c);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units. No effect.`);
      ctx.onDone?.(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit. It gets AP-5 during this battle.', candidates, (target) => {
      if (target) {
        applyApMod([target], -5, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP-5 during this battle.`);
        render();
        setupDropZones();
      }
      ctx.onDone?.();
    }, card, { mandatory: true });
  },
});

// ════════════════════════════════════════════════════════════════════════════
// GD06 follow-up — newly added cards
// ════════════════════════════════════════════════════════════════════════════

// ─── GD06-099 · Chang Wufei — Pilot ──────────────────────────────────────────
// [Attack] Choose 1 enemy Unit that is Lv.5 or higher. It gets AP-3 during
// this battle. No [During Link] qualifier printed — fires on any attack
// while paired, same convention as GD05-096 Chad Chadan.
registerCardEffect('GD06-099', {
  attack: (card, ctx) => {
    if (!ctx || ctx.attackerSide !== 'player') { ctx?.onDone?.(); return; }
    const candidates = state.oppField.filter(c => c && (parseInt(c.level) || 0) >= 5);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units Lv.5 or higher. No effect.`);
      ctx.onDone(); return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit that is Lv.5 or higher. It gets AP-3 during this battle.', candidates, (target) => {
      if (target) {
        applyApMod([target], -3, 'battleApMod');
        addLog(`${card.name} — ${target.name} gets AP-3 during this battle.`);
        render();
        setupDropZones();
      }
      ctx.onDone();
    }, card, { mandatory: true });
  },
});

// ─── GD06-064 · Altron Gundam (EW) — Unit ────────────────────────────────────
// <Blocker> native (nativeKeyword fixed in the DB — was blank).
// While an enemy Unit that is Lv.5 or higher is in play, this Unit gets AP+3.
// passiveUnitApBonus evaluated live in effectiveBp() — same convention as
// GD03-045 Balient — so the bonus tracks the enemy field automatically.
registerCardEffect('GD06-064', {
  passiveUnitApBonus: (card, ownerSide) => {
    const enemyField = ownerSide === 'player' ? state.oppField : state.playerField;
    return enemyField.some(c => c && (parseInt(c.level) || 0) >= 5) ? 3 : 0;
  },
});

// ─── GD06-119 · Sturm und Drang — Command Pilot ─────────────────────────────
// [Action] Choose 1 enemy Unit with 3 or less HP. Rest it. After activating
// this card's [Action], you may pair this card from your trash with one of
// your (MF) Units.
// Same "resolve effect, then offerPairFromTrash" shape as GD05-121/122
// Cyclone Punch/Graviton Hammer above (offerPairFromTrash is defined in
// red.js and shared across all color files).
registerCardEffect('GD06-119', {
  canActivate: () => state.oppField.some(c => c && effectiveHp(c) <= 3),
  action: (card, ctx) => {
    const candidates = state.oppField.filter(c => c && effectiveHp(c) <= 3);
    if (candidates.length === 0) {
      addLog(`${card.name} — no enemy Units with 3 or less HP. No effect.`);
      offerPairFromTrash(card, '(MF)', ctx.onDone);
      return;
    }
    showCardPickerModal(card.name, 'Choose 1 enemy Unit with 3 or less HP. Rest it.', candidates, (target) => {
      if (target) {
        setRested(target, true);
        addLog(`${card.name} — rested ${target.name}.`);
        render(); setupDropZones();
      }
      offerPairFromTrash(card, '(MF)', ctx.onDone);
    }, card, { mandatory: true });
  },
});

// ─── GD06-100 · Corin Nander — Pilot ────────────────────────────────────────
// [Burst] Add this card to your hand. (generic)
// When playing this card from your hand and pairing it with a (Dianna
// Counter) Unit that is Lv.5 or higher, it's playable without paying any
// RES (cost 0), not just a partial cost -1 discount.
// Same pilotCostOverride/handCostReduction two-hook shape as GD06-086
// Mashymre Cello: pilotCostOverride decides the REAL charged cost once a
// specific Unit is actually chosen (checked in PAIR_PILOT, game.js, before
// spendResources is called); handCostReduction only reports "a discount is
// available" for the hand-row preview badge (effectiveHandCost/buildCardEl's
// _handModified) — it can't know which Unit will actually be picked, so it
// fires whenever ANY eligible Unit is already in play.
registerCardEffect('GD06-100', {
  handCostReduction: (pilot) =>
    state.playerField.some(c => c && (c.trait || '').includes('(Dianna Counter)') && (parseInt(c.level) || 0) >= 5)
      ? (parseInt(pilot.cost) || 0)
      : 0,
  pilotCostOverride: (pilot, unitCard) =>
    ((unitCard.trait || '').includes('(Dianna Counter)') && (parseInt(unitCard.level) || 0) >= 5)
      ? 0
      : (parseInt(pilot.cost) || 0),
});
